/*
 * Multi-model background removal.
 *
 * Flow:
 *   1. Caller provides (sourceUrl, imgixBgRemoveUrl) after upload.
 *   2. We fetch three results in parallel: imgix's own bg-remove, PixelCut
 *      via imgix (bg-remove-model=4), and Ideogram via the AWS proxy Lambda.
 *   3. Each result is loaded once; its alpha bounding box drives a tight
 *      server-side `rect=` display crop. No quality scoring (removed
 *      2026-07-27) — slots are FIXED: imgix -> PixelCut -> Ideogram.
 *   4. Any result whose image never decoded is dropped (ok:false) so a dead
 *      model can't render as a blank option card.
 *   5. User pick becomes the preview src + Size Guide mockups.
 *
 * API keys for Gram and the AI-edit provider (PC) live in AWS Lambda env vars,
 * reached through API Gateway. The storefront only knows the proxy URL.
 */
(function () {
  'use strict';

  // All bg-removal API calls go through a single AWS Lambda (via API Gateway)
  // so the underlying provider and Gram keys stay server-side. The Lambda
  // accepts { model, imageUrl } and returns { url } — uniform contract for
  // every model. CORS on API Gateway restricts callers to the storefront
  // domains, so even though this URL is visible to visitors in DevTools,
  // they can't make Gram/provider calls outside those origins.
  var BG_REMOVAL_PROXY_URL = 'https://ruhg140mmf.execute-api.us-east-1.amazonaws.com/';

  // Settings
  var FAL_TIMEOUT_MS = 25000;            // give up on a model after 25s

  // Friendly labels for the "_BG Removal Service" line-item property shown
  // on the admin order detail page. The line-item property name itself is
  // intentionally not visible to customers (we don't show it in the cart UI).
  var BG_REMOVAL_SERVICE_NAMES = {
    ix: 'IX',
    gram: 'Gram',
    pc: 'PC'
  };

  // Cache results per source URL so toggling Remove Background off/on doesn't
  // re-run the AI-edit provider for the same uploaded file.
  var cache = new Map();

  /* ── NINJA AI (Owen 2026-10-28: every old remover → Wil's Ninja AI remover).
     Legacy templates (blanks, UV sticker packs, …) opt in via
     window.__ntBgrLegacyNinja = {client, provider} (layout/theme.liquid, set
     only when nt-bgr-template returns blank — gang/stock pages keep Wil's
     stock provider, common pages use nt-bgr-engine.js). One ranked job fills
     the three slots as Rank 1/2/3 (keys n1/n2/n3 — NOT ix/pc, those are
     "imgix-served" and would get imgix params stamped). Production = Wil's
     saved native file (NtBgrStockProvider.savedUrl: displayBox rect, else
     trim). Vectors and any Ninja failure keep the old ix/pc/gram path. */
  var NINJA_SERVICE = {
    's3od-binary8k-e10': 'ninja_ai_8KBI', 's3od-binary51544-e40': 'ninja_ai_51KBI',
    'max-local-regular-e30': 'ninja_ai_40KRG', 's3od-removal22890-c10': 'ninja_ai_23KRO'
  };
  var ninjaFailed = {};   // sourceUrl -> true: this source runs the old path
  var ninjaClientP = null;
  function ninjaEligible(sourceUrl) {
    var cfg = window.__ntBgrLegacyNinja;
    return !!(cfg && cfg.client && cfg.provider && sourceUrl && !ninjaFailed[sourceUrl] &&
      /\.(png|jpe?g|jfif|bmp|webp|heic|heif|avif|psd|gif|tiff?)$/i.test(String(sourceUrl).split('?')[0]));
  }
  function loadScriptOnce(src, ready) {
    return new Promise(function (resolve, reject) {
      if (ready()) return resolve();
      var s = document.createElement('script');
      s.src = src; s.async = true;
      s.onload = function () { ready() ? resolve() : reject(new Error('script loaded without its global')); };
      s.onerror = function () { reject(new Error('script failed: ' + src)); };
      document.head.appendChild(s);
    });
  }
  // One client per page (Wil): receipts + in-flight work stay in its memory.
  // Loaded on first use, so pages that never remove a background pay nothing.
  function ninjaClient() {
    if (ninjaClientP) return ninjaClientP;
    var cfg = window.__ntBgrLegacyNinja;
    ninjaClientP = loadScriptOnce(cfg.client, function () { return !!window.NinjaBackgroundRemoval; })
      .then(function () { return loadScriptOnce(cfg.provider, function () { return !!window.NtBgrStockProvider; }); })
      .then(function () { return ninjaDiagnostics(); })
      .then(function () {
        // Same diagnostics wiring as Wil's stock provider (onDiagnostic -> record), into the
        // legacy recorder (nt-bgr-legacy-diagnostics.js) since this page has no theme-hooks boot.
        return window.NinjaBackgroundRemoval.create({ onDiagnostic: function (name, fields) {
          try { var d = window.NtBgrLegacyDiagnostics; if (d) d.record(name, fields); } catch (e) {}
        } });
      });
    ninjaClientP.catch(function () { ninjaClientP = null; });
    return ninjaClientP;
  }
  // Operational diagnostics (Wil's runtime + receiver) for this legacy page. Best effort:
  // a missing/failed recorder never blocks or changes removal.
  var ninjaDiagP = null;
  function ninjaDiagnostics() {
    var d = window.__ntBgrLegacyNinja && window.__ntBgrLegacyNinja.diagnostics;
    if (!d || !d.recorder) return Promise.resolve(null);
    ninjaDiagP = ninjaDiagP || loadScriptOnce(d.recorder, function () { return !!window.NtBgrLegacyDiagnostics; })
      .then(function () { return window.NtBgrLegacyDiagnostics.boot(d); })
      .catch(function () { return null; });
    return ninjaDiagP;
  }
  // The receiver only accepts design ids shaped like the builders' ('f-<n>'), never file names.
  var ninjaDesignIds = {}, ninjaDesignSeq = 0;
  function ninjaDesignId(sourceUrl) {
    return ninjaDesignIds[sourceUrl] || (ninjaDesignIds[sourceUrl] = 'f-' + (++ninjaDesignSeq));
  }
  /* NO OLD-REMOVER PREVIEW (Owen 2026-10-29). The legacy upload engines
     (bySize_popular / __blanksUploadHelper / bySize / pdp_transferBySize*, plus
     nt-measure's sizing render) paint the hero with imgix `bg-remove=true` the
     moment the toggle is on, which showed (and billed) an old-remover render for
     a few seconds before Ninja Rank 1 replaced it. On Ninja pages, any <img>
     request for an imgix upload carrying bg-remove params is loaded WITHOUT them
     while Ninja is handling that source: the hero shows the original until Rank 1
     lands (the picker's spinners cover the wait). Image loads only: cart/payload
     strings are untouched (the cart hook swaps in the Ninja pick and waits for it).
     Vectors and sources whose Ninja run failed keep the old URL (the fallback). */
  function stripOldRemoval(url) {
    if (typeof url !== 'string' || url.indexOf('bg-remove') < 0 || !window.__ntBgrLegacyNinja) return url;
    if (!/^https:\/\/ninjauploads-production\.imgix\.net\//.test(url)) return url;
    var bare = url.split('?')[0];
    if (!ninjaEligible(bare)) return url;
    var q = url.slice(bare.length + 1).split('&').filter(function (kv) {
      return kv && !/^bg-remove(-model)?=/.test(kv);
    }).join('&');
    return q ? bare + '?' + q : bare;
  }
  (function installPreviewFilter() {
    try {
      var desc = Object.getOwnPropertyDescriptor(HTMLImageElement.prototype, 'src');
      if (desc && desc.set && !HTMLImageElement.prototype.__ntNoOldRemoval) {
        Object.defineProperty(HTMLImageElement.prototype, 'src', {
          configurable: true, enumerable: desc.enumerable, get: desc.get,
          set: function (v) { desc.set.call(this, stripOldRemoval(v)); }
        });
        var setAttr = Element.prototype.setAttribute;
        Element.prototype.setAttribute = function (name, value) {
          if (this instanceof HTMLImageElement && String(name).toLowerCase() === 'src') value = stripOldRemoval(String(value));
          return setAttr.call(this, name, value);
        };
        HTMLImageElement.prototype.__ntNoOldRemoval = true;
      }
    } catch (e) { /* never block the page */ }
  })();

  // In-flight Ninja jobs. The cart hook waits for these so an add-to-cart that
  // lands before Rank 1 never ships the imgix seed (old removal) as production.
  var ninjaInFlight = [];
  function ninjaSettled(maxMs) {
    if (!ninjaInFlight.length) return null;
    // Only when the selection is still the imgix seed and removal is on.
    var sel = window.__ntBgRemovalSelected;
    if (sel && sel.ninja) return null;
    try { if (!isBgRemoveToggleOn()) return null; } catch (e) {}
    var all = Promise.all(ninjaInFlight.map(function (p) { return p.then(null, function () {}); }));
    return Promise.race([all, new Promise(function (r) { setTimeout(r, maxMs); })]);
  }
  function runNinja(sourceUrl, emitReady) {
    var job = runNinjaJob(sourceUrl, emitReady);
    ninjaInFlight.push(job);
    var done = function () { ninjaInFlight = ninjaInFlight.filter(function (p) { return p !== job; }); };
    job.then(done, done);
    return job;
  }
  function runNinjaJob(sourceUrl, emitReady) {
    var cacheKey = 'ninja|' + sourceUrl;
    var cached = cache.get(cacheKey) || persistGet(cacheKey);
    if (cached) {
      cache.set(cacheKey, cached);
      cached.all.forEach(function (c) { emitReady(c); });
      return Promise.resolve(cached);
    }
    var name = String(sourceUrl).split('?')[0].split('/').pop();
    var file = { id: ninjaDesignId(sourceUrl), s3Url: sourceUrl, uploadName: name };
    var diag = function () { return window.NtBgrLegacyDiagnostics; };
    return ninjaClient().then(function (client) {
      // Mirrors nt-bgr-stock-provider.js: requested() before the job (sets file.ntBgrAttemptId,
      // which the client stamps on its own events), failure() if it throws.
      try { if (diag()) diag().requested(file); } catch (e) {}
      return client.remove(file);
    }).then(function (result) {
      var P = window.NtBgrStockProvider;
      var order = result.order || result.all.map(function (c) { return c.name; });
      var picks = [];
      order.forEach(function (k) {
        var c = result.all.find(function (x) { return x.name === k; });
        if (!c || !/^ninja/.test(c.name) || !c.persistentUrl || picks.length >= 3) return;
        try { picks.push({ c: c, saved: P.savedUrl(c), display: P.displayUrl(c) }); } catch (e) { /* unsaved: skip */ }
      });
      try { if (result.dispose) result.dispose(); } catch (e) {}
      if (!picks.length) throw Object.assign(new Error('Ninja AI returned no saved candidates'), { code: 'save_unavailable' });
      return Promise.all(picks.map(function (p, i) {
        return loadCandidate(p.display).then(function (d) {
          return emitReady({
            name: 'n' + (i + 1), url: d.displayUrl, persistentUrl: p.saved, bareUrl: sourceUrl, ok: d.ok,
            ninja: true, ninjaKey: p.c.name, service: NINJA_SERVICE[p.c.modelId] || 'Unknown'
          });
        });
      }));
    }).then(function (all) {
      all = all.filter(function (c) { return c.ok !== false; });
      if (!all.length) throw Object.assign(new Error('Ninja AI candidates failed to decode'), { code: 'preview_decode_failed' });
      try { if (diag()) diag().removalReady(file); } catch (e) {}
      var result = { winner: all[0], alternatives: all.slice(1), all: all };
      cache.set(cacheKey, result);
      persistSet(cacheKey, result);
      return result;
    }).catch(function (err) {
      try { if (diag()) diag().failure(file, 'removal', (err && err.code) || 'unavailable'); } catch (e) {}
      throw err;
    });
  }

  /* Models imgix serves via a URL PARAM, so the full-resolution ORDER url can
     reproduce them without any Lambda-hosted file.
       pc   -> 4 (fal-pixelcut). Its PREVIEW is also an imgix render
               (imgixModelCandidate in run()) — keep those in sync.
       gram -> 3 (fal-ideogram). Its PREVIEW stays on the LAMBDA (small input,
               ~10s flat — see callBgRemoval); only PRODUCTION uses the param,
               because imgix always runs the model on the FULL source (45.8s
               measured on a 25MP file) which is fine print-side but must never
               gate the picker. ⚠ Do NOT "simplify" gram's preview to an
               imgixModelCandidate — that reintroduces the 46s customer wait.
     Same-model contract: Lambda 'gram' and bg-remove-model=3 are both
     fal-Ideogram; if either side ever changes vendor, change both. */
  /* ⚠ gram REMOVED 2026-07-29 (was gram: 3): imgix does NOT compose
     trim=colorUnlessAlpha with bg-remove-model=3 — measured on one source:
     default model 1902px trimmed, model 4 2406px trimmed, model 3 = 4000px
     FULL FRAME untrimmed. Production sizes prints by pixel dims, so model-3
     files printed at the wrong physical size (Owen caught it in output).
     Until imgix fixes the composition (ticket filed), gram production =
     the Lambda-hosted PNG + trim (Owen's pipeline: Lambda removes, imgix
     trims the hosted file — trim composes correctly there). Cost: ~2000px
     ceiling (fal caps output), soft only on large prints; sharp ≤ ~6.7in.
     Do NOT re-add gram: 3 until the imgix fix is VERIFIED trimmed. */
  var IMGIX_MODEL_PARAM = { pc: 4 };

  /* The slow model: production renders of it are pre-warmed (below) so nobody
     downstream eats the cold-render time. */
  var SLOW_MODEL_NUM = 3;

  /* Fire-and-forget cache warm for a freshly stamped production url. imgix
     computes the REMOVAL once per source+params and reuses it across output
     sizes (measured: novel size on a warmed source = ~0.3-0.6s vs ~46s cold
     for model 3), so a tiny w=64 request here pays the 46s in the background
     — while the customer is sizing/checking out — and production's later
     full-res open is instant. Harmless duplicate: repeat warms hit the cache. */
  function warmProductionRender(url) {
    try {
      var w = new Image();
      w.src = url + (url.indexOf('?') >= 0 ? '&' : '?') + 'w=64';
    } catch (_) { /* warm is best-effort only */ }
  }

  /* Stamp the model selector onto a production url. The order url only carries
     `bg-remove=true`, which is imgix's DEFAULT model — without this, production
     would print imgix's removal while the customer picked PixelCut/Ideogram.
     Guards: only when the removal is actually on this url, and never twice
     (the cart hook can see the same payload more than once — the double-stamp
     guard also means the warm fires once per payload, not twice). */
  function stampModelParam(url, modelKey) {
    var num = modelKey && IMGIX_MODEL_PARAM[modelKey];
    if (!num || !url || typeof url !== 'string') return url;
    if (!/[?&]bg-remove=true/.test(url)) return url;
    if (/[?&]bg-remove-model=/.test(url)) return url;
    var stamped = url + '&bg-remove-model=' + num;
    if (num === SLOW_MODEL_NUM) warmProductionRender(stamped);
    return stamped;
  }

  /* DURABLE RESULT STORE (Owen 2026-09-03 "we store and have all bg remove
     options for any upload — why are we not just re-serving those results").
     Upgraded from the 2026-07-27 sessionStorage mirror to localStorage so a
     completed run is re-served on ANY later visit on this browser, not just
     across reloads in one tab. What that buys:
     - Ideogram is GENERATIVE: two runs on the same input produce different
       cutouts, so a re-run is not a refresh — it is a different result. The
       store is what keeps "the options you saw" stable across sessions
       (including a later reorder of the same source file).
     - The Ideogram call is also a paid Lambda round-trip; ix/pc come back off
       CDN cheaply either way.
     The original sessionStorage choice feared serving stale URLs to a later
     visit. persistentUrl is CDN-durable by design (the cart depends on that),
     so durability is safe — the TTL below is the guard for the residual risk
     of hosted-asset lifecycle changes, and the picker's <img> onerror chain
     degrades gracefully if a stored URL ever does die.

     Contract (unchanged from v1 apart from the envelope):
     - Values are plain strings/booleans (candidates hold name / url /
       persistentUrl / bareUrl / ok) so they are JSON-safe. If a future field
       is ever non-serialisable, bump PERSIST_V.
     - PARTIAL runs are never stored (same rule as the in-memory eviction), so
       a 1-option picker cannot become sticky.
     - Every access is try/caught: Safari private mode and quota-exceeded both
       throw, and any miss simply falls through to a live run.
     - Entries carry savedAt; expired or unparseable entries are dropped on
       read. Pruning is oldest-savedAt-first across OUR keys only. */
  var PERSIST_V = 'ntbgr:v2:';
  /* Cap sized from MEASURED entries, not guessed: a real stored run is
     ~2.5KB (plain imgix URLs — the candidates' `url` is a rect= crop URL,
     NOT the base64 the older comment above run() implies), and the whole
     origin sat at ~282KB of the ~5MB quota when measured (2026-09-03). 200
     entries is ~500KB worst case — roomy, while still bounded so this store
     can never crowd the origin. */
  var PERSIST_MAX = 200;                       // entries kept; oldest pruned
  var PERSIST_TTL = 90 * 24 * 60 * 60 * 1000;  // 90 days
  function persistGet(key) {
    try {
      var raw = localStorage.getItem(PERSIST_V + key);
      if (!raw) return null;
      var o = JSON.parse(raw);
      if (!o || !o.all || !o.all.length) return null;
      if (o.savedAt && (Date.now() - o.savedAt) > PERSIST_TTL) {
        try { localStorage.removeItem(PERSIST_V + key); } catch (e2) {}
        return null;
      }
      return o;
    } catch (e) { return null; }
  }
  function persistSet(key, result) {
    try {
      localStorage.setItem(PERSIST_V + key, JSON.stringify({
        winner: result.winner,
        alternatives: result.alternatives,
        all: result.all,
        savedAt: Date.now()
      }));
      // Prune oldest of OUR keys only — never touch another app's storage.
      var mine = [];
      for (var i = 0; i < localStorage.length; i++) {
        var k = localStorage.key(i);
        if (k && k.indexOf(PERSIST_V) === 0) {
          var at = 0;
          try { at = (JSON.parse(localStorage.getItem(k)) || {}).savedAt || 0; } catch (e3) {}
          mine.push({ k: k, at: at });
        }
      }
      mine.sort(function (a, b) { return a.at - b.at; });
      while (mine.length > PERSIST_MAX) localStorage.removeItem(mine.shift().k);
    } catch (e) { /* quota / private mode — this store is best-effort */ }
  }

  /* ── CROSS-PLATFORM RESULT STORE (Owen 2026-09-03: "this can't be browser
     or session based, this has to live somewhere it works cross platform").
     A completed run's candidate set is written as a small JSON manifest into
     the UPLOADS BUCKET at a key derived from the source basename — so any
     device, any session, re-serves the same three cutouts (Ideogram is
     generative: a re-run is a DIFFERENT result, and a paid Lambda call).

     Why the bucket and not a new backend: the presign endpoint signs a PUT
     for any filename VERBATIM (proven: sourceUrl === host + file_name), the
     bucket is publicly readable cross-origin (it serves the artwork itself),
     and source basenames are already globally unique (…____image_<ts>_<rand>).
     Zero backend deploys; the manifest is exactly as exposed as the artwork
     it describes. Manifests never appear in the design library — fetchDataNew
     reads the uploads DB, and nothing here calls saveData.

     Ordering: memory → localStorage → THIS → live run. A remote hit is
     promoted into both local layers; a remote miss costs one fast 403/404.
     Writes are fire-and-forget and only for COMPLETE runs (same rule as the
     local store). Last-write-wins if two devices race a first run — either
     manifest is a valid set, and every later device re-serves one of them. */
  var REMOTE_STORE_HOST = 'https://ninja-services-production-ninjauploadss3bucket-zks2mguobhe4.s3.amazonaws.com/';
  var REMOTE_PRESIGN = 'https://hpz51rjda5.execute-api.us-east-1.amazonaws.com/production/uploads/getPresignedUploadUrl';
  function remoteKey(cacheKey) {
    try {
      var base = String(cacheKey || '').split('?')[0].split('/').pop();
      try { base = decodeURIComponent(base); } catch (e2) {}
      /* Sanitized the SAME way on every device or the key stops being shared.
         Uniqueness survives sanitizing — the ____image_<ts>_<rand> core is
         already URL-safe. */
      base = base.replace(/[^A-Za-z0-9._-]/g, '-');
      if (!base || base.length > 180) return null;
      return 'bgrset__' + base + '.json';
    } catch (e) { return null; }
  }
  function remoteGet(cacheKey) {
    var k = remoteKey(cacheKey);
    if (!k) return Promise.resolve(null);
    return fetch(REMOTE_STORE_HOST + k)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (o) { return o && o.all && o.all.length ? o : null; })
      ['catch'](function () { return null; });
  }
  function remotePut(cacheKey, result) {
    var k = remoteKey(cacheKey);
    if (!k) return;
    try {
      fetch(REMOTE_PRESIGN + '?file_name=' + encodeURIComponent(k) +
            '&file_type=' + encodeURIComponent('application/json'))
        .then(function (r) { return r.json(); })
        .then(function (p) {
          if (!p || !p.url) return;
          var body = JSON.stringify({
            v: 1, savedAt: Date.now(),
            winner: result.winner, alternatives: result.alternatives, all: result.all
          });
          return fetch(p.url, { method: 'PUT', body: new Blob([body], { type: 'application/json' }) });
        })['catch'](function () { /* best-effort */ });
    } catch (e) { /* best-effort */ }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // AI-edit provider calls
  // ─────────────────────────────────────────────────────────────────────────

  // Single uniform call to the AWS proxy. Storefront posts { model, imageUrl }
  // and gets back { url } regardless of which upstream service the Lambda hit.
  //
  // ⚠ PREVIEW INPUT IS CAPPED AT 2000px — do NOT hand this the bare source.
  // Ideogram's runtime scales with INPUT area: measured ~11s at ~3MP but 45.8s
  // at 25MP (6144x4096), and this chain has two hard ceilings (the Lambda's
  // own ~25s upstream abort — the 502 "This operation was aborted" — and API
  // Gateway's 29s sync limit). Full-size inputs therefore 502'd on exactly the
  // big files customers care about, and bought nothing: fal returns a
  // ~2000-2600px result no matter what we send. Capping the input makes the
  // preview ~10s flat at any upload size. fit=max = never upscale small files.
  // The url is always an imgix url (bareUrl), so params render server-side.
  //
  // ⚠ SAME-MODEL CONTRACT: this Lambda's 'gram' upstream and the production
  // url's `bg-remove-model=3` (IMGIX_MODEL_PARAM) are BOTH fal-Ideogram. The
  // customer previews this model and production prints via that param — if
  // either side ever changes vendor, change BOTH or the preview lies.
  function callBgRemoval(model, imageUrl) {
    var ctrl = new AbortController();
    var timer = setTimeout(function () { ctrl.abort(); }, FAL_TIMEOUT_MS);
    var previewInput = imageUrl +
      (imageUrl.indexOf('?') >= 0 ? '&' : '?') + 'w=2000&fit=max';

    return fetch(BG_REMOVAL_PROXY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ model: model, imageUrl: previewInput }),
      signal: ctrl.signal
    }).then(function (resp) {
      clearTimeout(timer);
      if (!resp.ok) {
        return resp.text().then(function (t) {
          throw new Error(model + ' HTTP ' + resp.status + ': ' + t.slice(0, 200));
        });
      }
      return resp.json();
    }).then(function (data) {
      if (!data || !data.url) throw new Error(model + ' response missing url');
      return data.url;
    }).catch(function (err) {
      clearTimeout(timer);
      throw err;
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Alpha-mask scoring
  // ─────────────────────────────────────────────────────────────────────────

  function loadImage(url) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('Failed to load: ' + url)); };
      img.src = url;
    });
  }

  /* Candidate-load retry (Owen 2026-07-27 "sometimes only 1 or 2 bg removal
     options show"): a cold imgix bg-remove render can time out on the FIRST
     request during ingest lag (~5s+ on fresh uploads), and a single onerror
     used to drop that model from the picker for the whole session — an AI
     edit "fixed" it only because the new source URL forced a fresh run.
     Cold renders almost always land within ~10s, so retry the load up to 3
     more times with backoff before giving up. */
  var CANDIDATE_RETRY_DELAYS = [2000, 5000, 10000];
  function loadImageWithRetry(url, attempt) {
    attempt = attempt || 0;
    return loadImage(url).catch(function (e) {
      if (attempt >= CANDIDATE_RETRY_DELAYS.length) throw e;
      console.warn('[bg-removal-multi] candidate load retry ' + (attempt + 1) + ' in ' +
        CANDIDATE_RETRY_DELAYS[attempt] + 'ms for', url.slice(0, 80));
      return new Promise(function (res) { setTimeout(res, CANDIDATE_RETRY_DELAYS[attempt]); })
        .then(function () { return loadImageWithRetry(url, attempt + 1); });
    });
  }










  // ─────────────────────────────────────────────────────────────────────────
  // Orchestration
  // ─────────────────────────────────────────────────────────────────────────


  // Track the customer's selected version on a window-level var so the
  // /cart/add interceptor below can read both the model name AND the
  // persistent imgix URL at request time. Mirror to a hidden form input as
  // defense-in-depth for any code path that does submit the standard form.
  //
  // For multi-upload we ALSO maintain a per-source-URL map so each line
  // item in the cart-add payload can be matched to its own chosen version.
  // Without this, all items get the LAST-picked URL written into Upload,
  // which makes Shopify merge them into a single line item.
  if (!window.__ntBgRemovalSelectionsByBareUrl) {
    window.__ntBgRemovalSelectionsByBareUrl = {};
  }

  // Map of ALL generated bg-removal versions per source image, keyed by the
  // source bareUrl filename (same key shape as extractItemBareKey below).
  // We generate three versions for every upload (ix / gram / pc)
  // but only the chosen one becomes the Upload. This map lets the cart-add
  // interceptor attach the TWO versions the customer did NOT choose as
  // low-priority line-item properties (_IX Version / _Gram Version /
  // _PC Version).
  if (!window.__ntBgRemovalVersionsByBareUrl) {
    window.__ntBgRemovalVersionsByBareUrl = {};
  }
  // Cart-property label per engine key.
  var BG_VERSION_PROP_LABELS = {
    ix:   '_IX Version',
    gram: '_Gram Version',
    pc:   '_PC Version'
  };
  function bgRemovalSourceKey(url) {
    if (!url || typeof url !== 'string') return null;
    var k = url.split('?')[0].split('/').pop();
    return k ? k.toLowerCase() : null;
  }
  // Record one resolved candidate's persistent URL under its SOURCE key.
  function recordBgRemovalVersion(cand) {
    try {
      if (!cand || !cand.name || !cand.persistentUrl || !cand.bareUrl) return;
      if (!/^https?:\/\//.test(cand.persistentUrl)) return;
      var key = bgRemovalSourceKey(cand.bareUrl);
      if (!key) return;
      if (!window.__ntBgRemovalVersionsByBareUrl[key]) {
        window.__ntBgRemovalVersionsByBareUrl[key] = {};
      }
      window.__ntBgRemovalVersionsByBareUrl[key][cand.name] = cand.persistentUrl;
      if (cand.ninja) {
        var svc = window.__ntBgRemovalNinjaServiceByBareUrl || (window.__ntBgRemovalNinjaServiceByBareUrl = {});
        (svc[key] || (svc[key] = {}))[cand.name] = cand.service;
      }
    } catch (e) { /* ignore */ }
  }
  // Ninja AI picks: the same rank props Wil's stock provider writes
  // (_BG Removal Rank Choice + _Ninja AI Rank N Version / Model). Returns
  // [key, value] pairs so the JSON and form-encoded paths share it.
  function ninjaRankProps(srcBareKey, chosenKey) {
    var out = [];
    var versions = srcBareKey && window.__ntBgRemovalVersionsByBareUrl &&
                   window.__ntBgRemovalVersionsByBareUrl[srcBareKey];
    if (!versions || !/^n[1-3]$/.test(chosenKey || '')) return out;
    var svc = (window.__ntBgRemovalNinjaServiceByBareUrl || {})[srcBareKey] || {};
    out.push(['_BG Removal Rank Choice', 'Rank ' + chosenKey.slice(1)]);
    ['n1', 'n2', 'n3'].forEach(function (k) {
      if (!versions[k] || !/^https:\/\//.test(versions[k])) return;
      out.push(['_Ninja AI Rank ' + k.slice(1) + ' Version', versions[k]]);
      out.push(['_Ninja AI Rank ' + k.slice(1) + ' Model', svc[k] || 'Unknown']);
    });
    return out;
  }
  // Append the two non-chosen bg-removal version URLs to a line item's
  // properties (low priority — added last so they sit at the bottom of the
  // cart-item property list). Missing versions (a model that failed) are
  // skipped silently.
  function addAlternateVersionProps(props, srcBareKey, itemSel) {
    try {
      if (!props || !srcBareKey || !itemSel || !itemSel.modelKey) return;
      var versions = window.__ntBgRemovalVersionsByBareUrl &&
                     window.__ntBgRemovalVersionsByBareUrl[srcBareKey];
      if (!versions) return;
      var chosen = itemSel.modelKey; // 'ix' | 'gram' | 'pc' | 'n1'-'n3' (Ninja AI)
      if (itemSel.ninja) {
        ninjaRankProps(srcBareKey, chosen).forEach(function (kv) { props[kv[0]] = kv[1]; });
        return;
      }
      ['ix', 'gram', 'pc'].forEach(function (k) {
        if (k === chosen) return;                 // skip the version they chose
        var url = versions[k];
        if (url && /^https?:\/\//.test(url) && BG_VERSION_PROP_LABELS[k]) {
          props[BG_VERSION_PROP_LABELS[k]] = url; // appended last = low priority
        }
      });
    } catch (e) { /* ignore */ }
  }

  function setBgRemovalProperty(chosen) {
    var modelKey = typeof chosen === 'string' ? chosen : (chosen && chosen.name);
    var isObj = chosen && typeof chosen === 'object';
    var friendlyName = (isObj && chosen.service) || BG_REMOVAL_SERVICE_NAMES[modelKey] || modelKey;
    var persistentUrl = isObj ? chosen.persistentUrl : null;
    var bareUrl = isObj ? chosen.bareUrl : null;
    var record = {
      modelName: friendlyName,
      modelKey: modelKey,
      persistentUrl: persistentUrl,
      bareUrl: bareUrl,
      ninja: !!(isObj && chosen.ninja),
      selectedAt: Date.now()
    };
    window.__ntBgRemovalSelected = record;
    // Multi-upload key: source-image bareUrl (filename portion of the
    // imgix URL with query stripped). Lets the cart-add interceptor
    // look up the right candidate for each item independently.
    if (bareUrl) {
      var key = bareUrl.split('/').pop().toLowerCase();
      if (key) window.__ntBgRemovalSelectionsByBareUrl[key] = record;
    }
    var forms = document.querySelectorAll(
      'product-form.product-form form, form#cart, form[action*="/cart/add"]'
    );
    forms.forEach(function (form) {
      var input = form.querySelector('input[name="properties[_BG Removal Service]"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[_BG Removal Service]';
        form.appendChild(input);
      }
      input.value = friendlyName;
    });

    // NOTE: we deliberately do NOT save the chosen version to the customer's
    // design library here. Tab clicks/auto-applies are exploratory — the
    // customer might pick V1, then V2, then V3. We only commit the chosen
    // version to the library when they Add to Cart (handled by the cart-add
    // interceptor). If they upload and bail without adding to cart, only the
    // original (saved by bySize_popular at upload time) ends up in their
    // library — which matches the existing behavior for non-bg-removal cases.
  }

  // Cart-add interceptor: bySize_popular.js (and similar templates) build the
  // /cart/add payload via JS rather than submitting the standard product
  // form, so hidden inputs don't reach Shopify. We hook fetch + XHR globally
  // and inject `properties[_BG Removal Service]` into any request hitting the
  // cart-add endpoint. Only runs once per page load.
  // Captures the most recent /uploads/saveData payload so we can re-fire it
  // with the customer's chosen-version URL after they pick. Without this,
  // saveData runs at upload-completion time (before any model has finished),
  // so the bare original URL is what hits the customer's design library —
  // not the bg-removed result they actually chose.
  var SAVE_DATA_URL = 'https://hpz51rjda5.execute-api.us-east-1.amazonaws.com/production/uploads/saveData';
  var DELETE_DATA_URL = 'https://hpz51rjda5.execute-api.us-east-1.amazonaws.com/production/uploads/deleteData';
  var capturedSavePayload = null;
  var capturedOriginalPhotoId = null;
  var capturedCustomerId = null;
  var lastResavedUrl = null;
  var resaveTimer = null;

  // Captures metadata when the customer picks a PREVIOUS design (Use this
  // Design). bySize_popular.js doesn\'t fire /uploads/saveData in that path —
  // the design already lives in the customer\'s library — so capturedSavePayload
  // stays null and our re-save bails. Watching the previous-design selection
  // gives us everything we need (photo_id, file URL, file name, dimensions)
  // to construct a save payload ourselves and update the library entry to the
  // bg-removed version when the customer adds to cart.
  var capturedPrevDesign = null; // { photoId, fileName, fileUrl, width, height }

  function setCapturedPrevDesign(data) {
    if (!data || !data.photoId) return;
    capturedPrevDesign = {
      photoId: String(data.photoId),
      fileName: data.fileName || '',
      fileUrl: (data.fileUrl || '').split('?')[0],
      width: data.width || '',
      height: data.height || ''
    };
    // Reset the resave bookkeeping so the next add-to-cart actually fires
    // (we may be on the same image as a prior selection but the user has
    // since toggled bg-remove and picked a different version).
    lastResavedUrl = null;
    capturedOriginalPhotoId = null;
    capturedSavePayload = null;
    console.log('[bg-removal-multi] captured previous-design selection', capturedPrevDesign);
  }

  // Delegated click listener — catches every UI path that selects a previous
  // design:
  //   - Legacy popup: `.customer_designes ul li[data-photoid]` with a
  //     checkbox + bulk "Use Designs" button (bySize_popular renders this).
  //   - Modern picker: `.md-card[data-design-id]` inside dtf-upload-tabs /
  //     my-designs-panel — clicking the card triggers a synthetic click on
  //     a fake `.use_design[data-photoid]` anchor created by
  //     product-page-popups.liquid.
  //   - The synthetic `.use_design[data-photoid]` anchor itself (in case it
  //     bubbles before being removed from the DOM).
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    // Modern picker card.
    var mdCard = t.closest('.md-card[data-design-id]');
    if (mdCard && !mdCard.classList.contains('md-card--selection-disabled')) {
      var id = mdCard.getAttribute('data-design-id');
      var imgEl = mdCard.querySelector('img');
      var nameEl = mdCard.querySelector('.md-card__name');
      setCapturedPrevDesign({
        photoId: id,
        fileName: nameEl ? (nameEl.getAttribute('data-tooltip') || nameEl.textContent) : '',
        fileUrl: imgEl ? imgEl.getAttribute('src') : '',
        width: '',
        height: ''
      });
      return;
    }
    // Synthetic .use_design[data-photoid] anchor.
    var useDesign = t.closest('.use_design[data-photoid]');
    if (useDesign) {
      setCapturedPrevDesign({
        photoId: useDesign.getAttribute('data-photoid'),
        fileName: useDesign.getAttribute('data-name') || '',
        fileUrl: useDesign.getAttribute('data-src') || '',
        width: '',
        height: ''
      });
      return;
    }
    // Legacy popup row.
    var li = t.closest('.customer_designes ul li');
    if (li) {
      if (t.closest('.upload_tools a[data-id]')) return; // trash icon
      var photoId = li.getAttribute('data-photoid');
      if (!photoId) return;
      var checkbox = li.querySelector('.upload_tools .popupinput.checkbox-field');
      var useLink = li.querySelector('.use_design');
      var dataSrc = (checkbox && checkbox.getAttribute('data-src')) ||
                    (useLink && useLink.getAttribute('data-src')) || '';
      setCapturedPrevDesign({
        photoId: photoId,
        fileName: (checkbox && checkbox.getAttribute('data-name')) ||
                  li.getAttribute('data-name') || '',
        fileUrl: dataSrc,
        width: checkbox && checkbox.getAttribute('data-w'),
        height: checkbox && checkbox.getAttribute('data-h')
      });
    }
  }, true);

  // Try to pull a photo_id out of whatever response shape the API returns.
  // We've seen JSON responses use `photo_id`, `id`, or wrap inside `data`,
  // and the request body itself is a single-element array — so the response
  // may follow either shape too. Returns null if nothing recognizable.
  function extractPhotoId(respText) {
    if (!respText) return null;
    try {
      var data = JSON.parse(respText);
      if (typeof data !== 'object' || data === null) return null;
      if (Array.isArray(data)) data = data[0] || {};
      return data.photo_id || data.id ||
             (data.data && (data.data.photo_id || data.data.id)) ||
             null;
    } catch (e) { return null; }
  }

  function injectBgRemovalInSavePayload(body) {
    // Capture the payload shape (so we can re-fire later with chosen URL)
    // and pull customer_id out of it for the eventual deleteData call.
    if (typeof body === 'string') {
      try {
        capturedSavePayload = JSON.parse(body);
        var firstItem = Array.isArray(capturedSavePayload) ? capturedSavePayload[0] : capturedSavePayload;
        if (firstItem && firstItem.customer_id) capturedCustomerId = firstItem.customer_id;
      } catch (e) { /* ignore */ }
    }

    var sel = window.__ntBgRemovalSelected;
    if (!sel || !sel.persistentUrl || !/^https?:\/\//.test(sel.persistentUrl)) return body;
    if (typeof isBgRemoveToggleOn === 'function' && !isBgRemoveToggleOn()) return body;

    // Per-item rewrite keyed by the item's OWN source file, mirroring the
    // cart-add interceptor's __ntBgRemovalSelectionsByBareUrl lookup. The old
    // code stamped the single global selection's URL onto EVERY item — on a
    // multiupload (one saveData per file, all intercepted while one global
    // selection record exists) that saved N library records all pointing at
    // the SAME image. No per-source match → leave the item's original upload
    // URL alone (the cart-add resave path still commits the chosen version
    // later, so nothing is lost — the library just keeps the true original).
    function chosenUrlForSaveItem(fileUrl) {
      if (!fileUrl || typeof fileUrl !== 'string') return null;
      var key = bgRemovalSourceKey(fileUrl);
      var rec = key && window.__ntBgRemovalSelectionsByBareUrl &&
                window.__ntBgRemovalSelectionsByBareUrl[key];
      if (rec && rec.persistentUrl && /^https?:\/\//.test(rec.persistentUrl)) {
        return rec.persistentUrl;
      }
      return null;
    }
    if (typeof body === 'string') {
      try {
        var json = JSON.parse(body);
        var rewrote = null;
        var items = Array.isArray(json) ? json : [json];
        items.forEach(function (it) {
          if (it && typeof it === 'object') {
            var chosen = chosenUrlForSaveItem(it.file);
            if (chosen) { it.file = chosen; rewrote = chosen; }
          }
        });
        if (rewrote) lastResavedUrl = rewrote;
        return JSON.stringify(json);
      } catch (e) { /* not JSON — pass through */ }
    }
    return body;
  }

  // Build a saveData payload from the captured previous-design selection.
  // Returns null if we don't have enough info. The shape mirrors
  // bySize_popular.js\'s saveFileToUser (single-element array) so the server
  // sees the same envelope. file_crc is intentionally empty — we don\'t have
  // it for previous designs, and an empty value still creates a usable
  // library entry; the dedupe path below removes the stale one anyway.
  // The site declares CUSTOMER_ID / PRODUCT_ID / isGangPage with `let`/`const`
  // in <script> tags (see snippets/bySize__js.liquid, layout/theme.liquid).
  // These live in the global LEXICAL environment — visible by name from any
  // script — but are NOT properties on `window`.
  //
  // Two-step lookup so we cover both the "let-declared global" case (visible
  // via direct lexical reference) and any weird case where new Function\'s
  // global lookup behaves differently.
  function readGlobal(name) {
    // Direct typeof — safe even if name is undeclared (typeof never throws).
    var direct;
    try {
      if (name === 'CUSTOMER_ID')      direct = typeof CUSTOMER_ID !== 'undefined' ? CUSTOMER_ID : undefined;
      else if (name === 'PRODUCT_ID')  direct = typeof PRODUCT_ID !== 'undefined' ? PRODUCT_ID : undefined;
      else if (name === 'isGangPage')  direct = typeof isGangPage !== 'undefined' ? isGangPage : undefined;
    } catch (e) { /* fall through to new Function fallback */ }
    if (typeof direct !== 'undefined' && direct !== null) return direct;
    try {
      return (new Function('return typeof ' + name + ' !== "undefined" ? ' + name + ' : undefined;'))();
    } catch (e) { return undefined; }
  }

  function buildPrevDesignSavePayload(chosenUrl) {
    if (!capturedPrevDesign) return null;
    var pd = capturedPrevDesign;
    var cid = readGlobal('CUSTOMER_ID');
    if (!cid) {
      console.warn('[bg-removal-multi] no CUSTOMER_ID available — cannot resave previous design');
      return null;
    }
    var variantTitle = (pd.width && pd.height)
      ? (pd.width + 'x' + pd.height)
      : '';
    var pid = readGlobal('PRODUCT_ID') || '';
    var isGang = !!readGlobal('isGangPage');
    return [{
      variant_title: variantTitle,
      file: chosenUrl,
      customer_id: cid,
      file_name: pd.fileName || '',
      file_crc: '',
      product_id: pid,
      gangsheet: isGang
    }];
  }

  // Re-fire saveData with the currently chosen version's URL.
  //
  // Two trigger paths:
  //   - Tab pick (debounced 1500ms): customer clicked V2/V3 in the chooser.
  //     Debounce so rapid V1→V2→V3 only sends one call after they settle.
  //   - Cart add (immediate=true): customer is committing — they\'re about
  //     to navigate to checkout, so we send the request RIGHT NOW with
  //     keepalive:true so the browser doesn\'t cancel it mid-flight.
  //
  // Two source paths:
  //   1. Fresh upload — bySize_popular fired /uploads/saveData, which we
  //      intercepted and stashed in capturedSavePayload. We mutate it.
  //   2. Previous design — bySize_popular did NOT fire saveData (the design
  //      already exists). We build a payload from capturedPrevDesign and use
  //      its photoId as the original-to-delete.
  function maybeResaveWithChosen(immediate) {
    var sel = window.__ntBgRemovalSelected;
    if (!sel || !sel.persistentUrl || !/^https?:\/\//.test(sel.persistentUrl)) {
      console.log('[bg-removal-multi] resave SKIP: no valid persistentUrl', sel);
      return;
    }
    if (typeof isBgRemoveToggleOn === 'function' && !isBgRemoveToggleOn()) {
      console.log('[bg-removal-multi] resave SKIP: bg-remove toggle is off');
      return;
    }
    if (lastResavedUrl === sel.persistentUrl) {
      console.log('[bg-removal-multi] resave SKIP: already resaved this URL');
      return;
    }

    var hasFresh = !!capturedSavePayload;
    var hasPrev = !!capturedPrevDesign;
    if (!hasFresh && !hasPrev) {
      console.log('[bg-removal-multi] resave SKIP: no captured source (fresh upload or previous design)');
      return;
    }
    console.log('[bg-removal-multi] resave PROCEED:',
      'hasFresh=', hasFresh, 'hasPrev=', hasPrev,
      'capturedPrevDesign=', capturedPrevDesign,
      'CUSTOMER_ID=', readGlobal('CUSTOMER_ID'));

    function doResave() {
      var current = window.__ntBgRemovalSelected;
      if (!current || lastResavedUrl === current.persistentUrl) return;

      var modified;
      var photoIdToDelete;
      var customerId;
      if (hasFresh) {
        var src = capturedSavePayload;
        var arr = Array.isArray(src) ? src : [src];
        modified = arr.map(function (it) {
          if (!it || typeof it !== 'object') return it;
          var copy = {};
          for (var k in it) if (Object.prototype.hasOwnProperty.call(it, k)) copy[k] = it[k];
          copy.file = current.persistentUrl;
          return copy;
        });
        photoIdToDelete = capturedOriginalPhotoId;
        customerId = capturedCustomerId;
      } else {
        modified = buildPrevDesignSavePayload(current.persistentUrl);
        if (!modified) {
          console.warn('[bg-removal-multi] no previous-design payload to resave');
          return;
        }
        photoIdToDelete = capturedPrevDesign.photoId;
        customerId = readGlobal('CUSTOMER_ID') || null;
      }

      lastResavedUrl = current.persistentUrl;
      console.log('[bg-removal-multi] re-saving design with chosen URL',
        hasFresh ? '(fresh upload)' : '(previous design)',
        immediate ? '[immediate]' : '[debounced]',
        'payload=', modified);
      // keepalive:true lets the request survive page navigation — critical
      // when this fires from the cart-add path, since the user is usually
      // about to redirect to /cart or /checkout.
      fetch(SAVE_DATA_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(modified),
        credentials: 'omit',
        keepalive: true
      }).then(function (resp) {
        // CRITICAL: fetch() only rejects on network errors. 4xx/5xx come back
        // as resolved with resp.ok === false. We MUST check resp.ok before
        // deleting the original — otherwise a failed save followed by a
        // successful delete leaves the customer with no library entry at all.
        if (!resp.ok) {
          return resp.text().then(function (text) {
            console.error('[bg-removal-multi] saveData rejected', resp.status, text);
            // Rollback so the next attempt actually re-fires.
            lastResavedUrl = null;
            throw new Error('saveData HTTP ' + resp.status);
          });
        }
        if (!photoIdToDelete || !customerId) {
          console.log('[bg-removal-multi] resave done; skipping delete (no photo_id/customer_id)');
          return;
        }
        var delUrl = DELETE_DATA_URL +
          '?customer_id=' + encodeURIComponent(customerId) +
          '&photo_id=' + encodeURIComponent(photoIdToDelete);
        console.log('[bg-removal-multi] deleting original design photo_id=', photoIdToDelete);
        return fetch(delUrl, { credentials: 'omit', keepalive: true }).then(function (delResp) {
          if (!delResp.ok) {
            console.warn('[bg-removal-multi] deleteData rejected', delResp.status);
            return;
          }
          if (hasFresh) {
            capturedOriginalPhotoId = null;
          } else {
            capturedPrevDesign = null;
          }
        });
      }).catch(function (err) {
        console.warn('[bg-removal-multi] resave/delete failed:', err && err.message);
      });
    }

    if (resaveTimer) { clearTimeout(resaveTimer); resaveTimer = null; }
    if (immediate) doResave();
    else resaveTimer = setTimeout(doResave, 1500);
  }

  // Pull design-re-used photo_id and width/height out of a cart-add payload
  // and stash them into capturedPrevDesign so the resave has what it needs.
  // The site already writes these into the cart-add properties:
  //   properties['design re-used'] = grt_photo_id (when re-using a design)
  //   properties['_width'] / properties['_height'] = numeric inches
  // Underscore-prefixed so they stay hidden from the checkout UI. Legacy
  // width / height keys are still read for items added before the rename.
  // This is the safety net if our click listener missed the picker UI.
  function harvestPrevDesignFromCartAddProps(props) {
    if (!props || typeof props !== 'object') return;
    var reused = props['design re-used'];
    if (!reused || /^previously on order/i.test(String(reused))) return;
    var photoId = String(reused);
    if (!capturedPrevDesign) capturedPrevDesign = { photoId: photoId };
    else if (!capturedPrevDesign.photoId) capturedPrevDesign.photoId = photoId;
    var w = props._width || props.width;
    var h = props._height || props.height;
    if (w && !capturedPrevDesign.width) capturedPrevDesign.width = String(w);
    if (h && !capturedPrevDesign.height) capturedPrevDesign.height = String(h);
    // Reset bookkeeping so the upcoming resave actually fires.
    if (lastResavedUrl) lastResavedUrl = null;
  }

  function harvestFromCartAddBody(body) {
    try {
      if (typeof body === 'string') {
        var first = body.charAt(0);
        if (first === '{' || first === '[') {
          var json = JSON.parse(body);
          if (json && Array.isArray(json.items)) {
            json.items.forEach(function (it) { harvestPrevDesignFromCartAddProps(it && it.properties); });
          } else if (json) {
            harvestPrevDesignFromCartAddProps(json.properties);
          }
        } else {
          // Form-encoded. Reconstruct just the properties[*] entries.
          var p = new URLSearchParams(body);
          var props = {};
          p.forEach(function (v, k) {
            var m = k.match(/^properties\[(.+)\]$/);
            if (m) props[m[1]] = v;
          });
          harvestPrevDesignFromCartAddProps(props);
        }
      } else if (typeof FormData !== 'undefined' && body instanceof FormData) {
        var fdProps = {};
        body.forEach(function (v, k) {
          var m = k.match(/^properties\[(.+)\]$/);
          if (m) fdProps[m[1]] = v;
        });
        harvestPrevDesignFromCartAddProps(fdProps);
      } else if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        var upProps = {};
        body.forEach(function (v, k) {
          var m = k.match(/^properties\[(.+)\]$/);
          if (m) upProps[m[1]] = v;
        });
        harvestPrevDesignFromCartAddProps(upProps);
      }
    } catch (e) { /* ignore — best effort */ }
  }

  // Pull the source bareUrl out of a line item's properties so we can match
  // it against the per-source selection map. Looks for the upload property
  // (with or without leading underscore) and returns just the filename
  // portion (lowercased) — same key shape setBgRemovalProperty uses.
  function extractItemBareKey(props) {
    if (!props || typeof props !== 'object') return null;
    var keys = Object.keys(props);
    for (var i = 0; i < keys.length; i++) {
      var k = keys[i];
      var lk = k.toLowerCase();
      if (lk.indexOf('upload') !== -1 && lk.indexOf('vector') !== -1) {
        var val = props[k];
        if (val && typeof val === 'string') {
          return val.split('?')[0].split('/').pop().toLowerCase();
        }
      }
    }
    return null;
  }

  // Look up the chosen selection for a given item (or fall back to the
  // last-picked global). Returns the candidate-like record with modelName /
  // modelKey / persistentUrl.
  function lookupSelectionForItem(props, strict) {
    var key = extractItemBareKey(props);
    if (key && window.__ntBgRemovalSelectionsByBareUrl &&
        window.__ntBgRemovalSelectionsByBareUrl[key]) {
      return window.__ntBgRemovalSelectionsByBareUrl[key];
    }
    // In strict mode (multi-item cart-add payloads), the absence of a per-source
    // match means THIS item had no background removed — so we must NOT fall back
    // to the last-picked global. Doing so writes the one chosen bg-removal URL
    // onto every line item, which makes Shopify merge them into a single line
    // item (the multi-upload "3 designs collapse to 1 image" bug).
    if (strict) return null;
    return window.__ntBgRemovalSelected || null;
  }

  function injectBgRemovalInPayload(body) {
    var sel = window.__ntBgRemovalSelected;
    if (!sel || !sel.modelName) {
      console.log('[bg-removal-multi] cart-add intercept: nothing selected, passing through');
      return body;
    }
    console.log('[bg-removal-multi] cart-add intercept fired; lastSel=', sel,
      'selectionsByBareUrl=', window.__ntBgRemovalSelectionsByBareUrl);
    // Pull design-re-used / width / height out of the cart-add payload so
    // capturedPrevDesign is fully populated even if our click listener
    // missed the picker. Then save the chosen version to the design library
    // IMMEDIATELY — the customer is about to navigate, so we use the
    // keepalive: true fetch inside maybeResaveWithChosen.
    harvestFromCartAddBody(body);
    console.log('[bg-removal-multi] after harvest, capturedPrevDesign=', capturedPrevDesign);
    maybeResaveWithChosen(true);

    // applyToProps is called per-item. It looks up the per-source selection
    // for that item via its Upload property, so each line item in a multi-
    // upload payload gets its OWN chosen URL (and thus survives in the cart
    // as a distinct line item instead of being merged with the others).
    //
    // Falls back to the last-picked global (window.__ntBgRemovalSelected) for
    // single-item payloads / form-encoded paths where per-item lookup isn\'t
    // available.
    function buildItemOverrides(itemSel) {
      itemSel = itemSel || sel;
      // Vector sources (svg/eps/pdf/ai) are what production prints, so the
      // ORIGINAL file must ALWAYS stay in (_)Upload (Vector Files Preferred) —
      // with or without bg-removal. A bg-removed RASTER result (gram = Option 2,
      // pc = Option 3) must NOT replace it with a PNG. itemSel.bareUrl is
      // the original pre-bg-remove source URL (run() sets it to sourceUrl; its
      // path keeps the real extension even when the preview is imgix-rasterized,
      // since bareUrlFromSrc strips the query). Leaving overrideUpload false
      // preserves the [BOTH_S3] original art file (and _cartImg); the
      // _BG Removal Service tag + alternate-version props below still record
      // which service the customer picked. (ix = Option 1 already skips it.)
      var isVectorSource = !!(itemSel && itemSel.bareUrl &&
                              /\.(?:svg|eps|pdf|ai)(?:[?#]|$)/i.test(itemSel.bareUrl));
      /* ALL THREE MODELS NOW REACH PRODUCTION LIKE OPTION 1 — RESOLUTION-
         CRITICAL. pc since 2026-07-27 (bg-remove-model=4), gram since
         2026-07-29 (bg-remove-model=3, "mirror imgix" — Owen): the production
         file is the full-resolution ORDER url with the removal applied by
         params at print time. NOTHING overrides Upload with a persistentUrl
         anymore:
           - pc/ix persistentUrls carry `w=2000` (thumbnail) — overriding sent
             production a 2000px file; a 22x22in/300dpi design printed ~91dpi.
           - gram's persistentUrl is the Lambda-hosted PREVIEW png, now built
             from a 2000px input (see callBgRemoval) — display-only by design.
         overrideUpload therefore stays for SAFETY (an unknown future model
         with a real hosted file), not for any current model. The vector guard
         below it is likewise belt-and-suspenders now: gram is excluded via
         IMGIX_MODEL_PARAM before isVectorSource is even consulted. */
      var overrideUpload = itemSel && itemSel.modelKey && itemSel.modelKey !== 'ix' &&
                           !IMGIX_MODEL_PARAM[itemSel.modelKey] &&
                           itemSel.persistentUrl &&
                           /^https?:\/\//.test(itemSel.persistentUrl) &&
                           !isVectorSource;
      /* Owen's pipeline (2026-07-29): Lambda removes, imgix TRIMS the hosted
         file. Trim composes correctly on a plain alpha asset — this is the
         interim for the model-3 trim bug (see IMGIX_MODEL_PARAM note). */
      /* trim-tol=10 is REQUIRED: Ideogram's "transparent" margins carry alpha
         noise of 2-4/255 (measured on a real order file), so zero-tolerance
         trim finds no transparent border and no-ops. tol=10 trimmed the same
         file 1254px -> 910px. Scoped to gram files only (this branch). */
      /* Ninja AI: persistentUrl is ALREADY Wil's production URL (saved native
         file + displayBox rect, or trim) — use it verbatim, no second trim. */
      var ninjaPick = overrideUpload && itemSel.ninja;
      var u = overrideUpload
        ? (ninjaPick ? itemSel.persistentUrl : itemSel.persistentUrl +
          (itemSel.persistentUrl.indexOf('?') >= 0 ? '&' : '?') +
          'trim=colorUnlessAlpha&trim-tol=10')
        : null;
      var c = u ? u + (u.indexOf('?') >= 0 ? '&' : '?') + 'auto=compress&q=50&h=150' : null;
      // Apply the Super Resolution / Upscale toggle.
      if (u) u = withUpscaleIfOn(u);
      if (c) c = withUpscaleIfOn(c);
      return {
        modelName: (itemSel && itemSel.modelName) || sel.modelName,
        uploadUrl: u,
        cartImgUrl: c
      };
    }

    // Shopify renders line-item properties in the admin in the order they
    // appear in the payload. To place `_BG Removal Service` directly under
    // `Remove Background`, we rebuild the properties object with our key
    // inserted right after that one. Other keys keep their existing order.
    function applyToProps(props, strict) {
      // Capture the SOURCE key + selection BEFORE the Upload override below.
      // Once we rewrite Upload (Vector Files Preferred) to the chosen
      // version's URL, extractItemBareKey would return the chosen version's
      // filename instead of the source — breaking the alternate-version
      // lookup. So resolve both up front.
      var srcBareKey = extractItemBareKey(props);
      var itemSelLookup = lookupSelectionForItem(props, strict);
      // Strict (multi-item) payload + this item has no bg-removal selection of
      // its own → leave its properties completely untouched: keep its own
      // uploaded image and add no _BG Removal Service tag. This is what stops
      // one item's chosen URL from bleeding onto the others.
      if (strict && !itemSelLookup) return;
      var perItem = buildItemOverrides(itemSelLookup);
      var newUploadUrl = perItem.uploadUrl;
      var newCartImgUrl = perItem.cartImgUrl;
      var modelName = perItem.modelName;
      // assets/hide-upload-property.js renames "Upload (Vector Files
      // Preferred)" → "_Upload (Vector Files Preferred)" at the cart-add
      // request boundary, and re-installs its fetch/XHR wrap on a setTimeout
      // — so it can end up either inside OR outside our wrap. Write to BOTH
      // keys so whichever survives carries the chosen URL. The cart-form-
      // page.liquid thumbnail loop matches any property whose key contains
      // "upload" (case-insensitive), so both names get picked up.
      // imgix-served models: stamp the selector on the EXISTING full-res order
      // url instead of replacing it (see the overrideUpload note above).
      var mkProps = itemSelLookup && itemSelLookup.modelKey;
      if (mkProps && IMGIX_MODEL_PARAM[mkProps]) {
        ['Upload (Vector Files Preferred)', '_Upload (Vector Files Preferred)', '_cartImg']
          .forEach(function (k) { props[k] = stampModelParam(props[k], mkProps); });
      }
      if (newUploadUrl) {
        props['Upload (Vector Files Preferred)'] = newUploadUrl;
        props['_Upload (Vector Files Preferred)'] = newUploadUrl;
      }
      if (newCartImgUrl) props['_cartImg'] = newCartImgUrl;

      // Build a new object inserting _BG Removal Service right after
      // Remove Background. We mutate `props` in place by deleting all keys
      // and re-adding them in the desired order — necessary because the
      // caller holds a reference to `props` (it\'s the item\'s properties
      // object inside the JSON payload).
      var keys = Object.keys(props);
      var ordered = [];
      var inserted = false;
      keys.forEach(function (k) {
        if (k === '_BG Removal Service') return; // we\'ll inject it explicitly
        ordered.push(k);
        if (k === 'Remove Background') {
          ordered.push('_BG Removal Service');
          inserted = true;
        }
      });
      // Fallback: if Remove Background wasn\'t in the payload, append at end.
      if (!inserted) ordered.push('_BG Removal Service');

      var values = {};
      ordered.forEach(function (k) {
        values[k] = k === '_BG Removal Service' ? modelName : props[k];
      });
      // Wipe + repopulate to preserve insertion order on the same reference.
      Object.keys(props).forEach(function (k) { delete props[k]; });
      ordered.forEach(function (k) { props[k] = values[k]; });

      // Append the two bg-removal versions the customer did NOT choose as
      // low-priority properties at the very bottom. srcBareKey was captured
      // before the Upload override above so it still points at the source.
      addAlternateVersionProps(props, srcBareKey, itemSelLookup);
    }

    // For form-encoded payloads (URLSearchParams/FormData) the admin uses
    // the order entries appear in the body. URLSearchParams doesn\'t support
    // insertion mid-list, so we rebuild from scratch.
    // Form-encoded payloads are single-item by definition (Shopify\'s legacy
    // /cart/add.js form path), so we just use the last-picked global.
    function applyToParams(params) {
      // Source key from the ORIGINAL Upload value (before the override below)
      // so we can attach the non-chosen versions.
      var origUpload = params.get('properties[Upload (Vector Files Preferred)]') ||
                       params.get('properties[_Upload (Vector Files Preferred)]') || '';
      var srcBareKey = origUpload ? bgRemovalSourceKey(origUpload) : null;
      var perItem = buildItemOverrides(sel);
      var newUploadUrl = perItem.uploadUrl;
      var newCartImgUrl = perItem.cartImgUrl;
      var modelName = perItem.modelName;
      var entries = [];
      params.forEach(function (v, k) {
        if (k === 'properties[_BG Removal Service]') return;
        entries.push([k, v]);
        if (k === 'properties[Remove Background]') {
          entries.push(['properties[_BG Removal Service]', modelName]);
        }
      });
      if (!entries.some(function (e) { return e[0] === 'properties[_BG Removal Service]'; })) {
        entries.push(['properties[_BG Removal Service]', modelName]);
      }
      // Apply Upload / _cartImg overrides on top of the rebuilt list.
      var byKey = {};
      entries.forEach(function (e, i) { byKey[e[0]] = i; });
      function setOrPush(key, val) {
        if (val == null) return;
        if (byKey.hasOwnProperty(key)) entries[byKey[key]][1] = val;
        else entries.push([key, val]);
      }
      // Set both visible and hidden Upload keys — see comment in applyToProps.
      // imgix-served models: stamp the selector on the EXISTING full-res order
      // url instead of replacing it (see the overrideUpload note above).
      var mkParams = sel && sel.modelKey;
      if (mkParams && IMGIX_MODEL_PARAM[mkParams]) {
        ['properties[Upload (Vector Files Preferred)]',
         'properties[_Upload (Vector Files Preferred)]',
         'properties[_cartImg]'].forEach(function (k) {
          if (byKey.hasOwnProperty(k)) {
            entries[byKey[k]][1] = stampModelParam(entries[byKey[k]][1], mkParams);
          }
        });
      }
      setOrPush('properties[Upload (Vector Files Preferred)]', newUploadUrl);
      setOrPush('properties[_Upload (Vector Files Preferred)]', newUploadUrl);
      setOrPush('properties[_cartImg]', newCartImgUrl);

      // Append the two non-chosen bg-removal versions (low priority — pushed
      // last). Mirrors addAlternateVersionProps for the form-encoded path.
      if (srcBareKey && sel && sel.modelKey &&
          window.__ntBgRemovalVersionsByBareUrl &&
          window.__ntBgRemovalVersionsByBareUrl[srcBareKey]) {
        var _versions = window.__ntBgRemovalVersionsByBareUrl[srcBareKey];
        if (sel.ninja) ninjaRankProps(srcBareKey, sel.modelKey).forEach(function (kv) {
          setOrPush('properties[' + kv[0] + ']', kv[1]);
        });
        else ['ix', 'gram', 'pc'].forEach(function (k) {
          if (k === sel.modelKey) return;
          var url = _versions[k];
          if (url && /^https?:\/\//.test(url) && BG_VERSION_PROP_LABELS[k]) {
            setOrPush('properties[' + BG_VERSION_PROP_LABELS[k] + ']', url);
          }
        });
      }

      // Clear all keys and re-add in computed order.
      var allKeys = [];
      params.forEach(function (_v, k) { allKeys.push(k); });
      allKeys.forEach(function (k) { params.delete(k); });
      entries.forEach(function (e) { params.append(e[0], e[1]); });
    }

    // FormData payload
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      applyToParams(body);
      return body;
    }
    // URLSearchParams payload
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      applyToParams(body);
      return body;
    }
    // String payload — try JSON first, fall back to form-encoded
    if (typeof body === 'string') {
      try {
        var json = JSON.parse(body);
        if (typeof json === 'object' && json !== null) {
          if (Array.isArray(json.items)) {
            json.items.forEach(function (it) {
              it.properties = it.properties || {};
              applyToProps(it.properties, true);  // strict: per-item only, no global fallback
            });
          } else {
            json.properties = json.properties || {};
            applyToProps(json.properties, false); // single-item: global fallback is safe
          }
          return JSON.stringify(json);
        }
      } catch (e) { /* not JSON — assume form-encoded */ }
      try {
        var params = new URLSearchParams(body);
        applyToParams(params);
        return params.toString();
      } catch (e) { /* give up and pass body through */ }
    }
    return body;
  }

  function installCartHook() {
    if (window.__ntCartHookInstalled) return;
    window.__ntCartHookInstalled = true;
    var matchesCartAdd = function (u) { return /\/cart\/add(\.js)?(\?|$)/.test(u || ''); };
    var matchesSaveData = function (u) { return /\/uploads\/saveData(\?|$)/.test(u || ''); };

    function rewriteBody(url, body) {
      if (matchesCartAdd(url)) return injectBgRemovalInPayload(body);
      if (matchesSaveData(url)) return injectBgRemovalInSavePayload(body);
      return body;
    }

    // Patch fetch
    if (typeof window.fetch === 'function') {
      var origFetch = window.fetch;
      window.fetch = function (input, init) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var wait = matchesCartAdd(url) && ninjaSettled(90000);
        // Rank 1 still running: hold the add (once, capped), then rewrite with the Ninja pick.
        if (wait) { var self = this; return wait.then(function () { return rewrittenFetch.call(self, input, init); }); }
        return rewrittenFetch.call(this, input, init);
      };
      var rewrittenFetch = function (input, init) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var needsRewrite = matchesCartAdd(url) || matchesSaveData(url);
        if (needsRewrite && init && init.body) {
          init = Object.assign({}, init, { body: rewriteBody(url, init.body) });
        } else if (needsRewrite && input && typeof input === 'object' && input.body && init === undefined) {
          try {
            var reqInit = {
              method: input.method,
              headers: input.headers,
              body: rewriteBody(url, input.body),
              credentials: input.credentials
            };
            return origFetch.call(this, url, reqInit);
          } catch (e) { /* fall through */ }
        }
        return origFetch.call(this, input, init);
      };
    }

    // Patch XHR
    if (typeof XMLHttpRequest !== 'undefined') {
      var XP = XMLHttpRequest.prototype;
      var origOpen = XP.open;
      var origSend = XP.send;
      XP.open = function (method, url) {
        try { this.__ntInterceptUrl = url; } catch (e) {}
        return origOpen.apply(this, arguments);
      };
      XP.send = function (body) {
        var w = !this.__ntWaited && matchesCartAdd(this.__ntInterceptUrl) && ninjaSettled(90000);
        if (w) { var x = this, a = arguments; x.__ntWaited = true; w.then(function () { XP.send.apply(x, a); }); return; }
        try {
          var url = this.__ntInterceptUrl;
          if (body && (matchesCartAdd(url) || matchesSaveData(url))) {
            body = rewriteBody(url, body);
          }
          // Capture the photo_id from saveData responses so we can deleteData
          // the original after the chosen-version save lands.
          if (matchesSaveData(url)) {
            var self = this;
            this.addEventListener('load', function () {
              try {
                if (self.status >= 200 && self.status < 300) {
                  var pid = extractPhotoId(self.responseText);
                  // Only capture if we haven't started the resave flow yet —
                  // we want the ORIGINAL save's photo_id, not the resave's.
                  if (pid && !capturedOriginalPhotoId && !lastResavedUrl) {
                    capturedOriginalPhotoId = pid;
                  }
                }
              } catch (e) { /* ignore */ }
            });
          }
        } catch (e) {}
        return origSend.call(this, body);
      };
    }
  }
  installCartHook();



  // Load a candidate image and return BOTH:
  //   - mask: alpha values at SCORE_SIZE for the UNTRIMMED image — used for
  //     scoring. We don't crop before scoring because (a) the variance check
  //     needs untrimmed pixels that align with the original image's pixels,
  //     and (b) cropping to alpha bbox always produces high-coverage masks
  //     which falsely fire the coverage penalty.
  //   - displayUrl: a data URL of the TRIMMED image — used for the preview
  //     and Size Guide mockups so the user sees tight crops, matching the
  //     visual goal of imgix's `trim=colorUnlessAlpha`.
  var ALPHA_TRIM_THRESHOLD = 16;
  function loadCandidate(url) {
    return loadImageWithRetry(url).then(function (img) {
      var w = img.naturalWidth || img.width;
      var h = img.naturalHeight || img.height;
      // ok:false === "this never decoded". run() drops these; see the
      // NO SCORING note there (loadCandidate resolves on failure, so without
      // an explicit flag a dead model renders as a blank option card).
      if (!w || !h) return { displayUrl: url, ok: false };

      // Full-resolution canvas for trim bbox computation.
      var fullCanvas = document.createElement('canvas');
      fullCanvas.width = w; fullCanvas.height = h;
      var fctx = fullCanvas.getContext('2d');
      fctx.drawImage(img, 0, 0);
      var fullData;
      try { fullData = fctx.getImageData(0, 0, w, h).data; }
      catch (e) {
        // The image DID decode — we just can't read its pixels (CORS). Still a
        // perfectly usable option, so ok stays true; we only lose the tight crop.
        console.warn('[bg-removal-multi] canvas tainted, using raw url for', url.slice(0, 80));
        return { displayUrl: url, ok: true };
      }

      // Compute alpha bounding box for the trimmed display URL.
      var minX = w, maxX = -1, minY = h, maxY = -1;
      for (var y = 0; y < h; y++) {
        var rowStart = y * w * 4;
        for (var x = 0; x < w; x++) {
          if (fullData[rowStart + x * 4 + 3] > ALPHA_TRIM_THRESHOLD) {
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
      }
      var displayUrl = url;
      /* Skip the client-computed `rect` crop whenever this render is SCALED.
         The alpha bbox below is measured in the LOADED render's pixels, but
         imgix applies `rect` in SOURCE pixels — so on any url carrying `w=`
         (or `h=`) the box is interpreted at a different scale and slices out
         the wrong region, cropping into the artwork.

         WIDENED 2026-07-27 (Owen saw Option 1 render 2 of 4 rows, zoomed in).
         This guard used to test for `bg-remove-model=`, which protected
         Option 2 only BY ACCIDENT — it happens to carry the model param.
         Option 1 has the identical `w=2000` scaling and was unprotected, so it
         cropped wrongly whenever the bbox came out smaller than the render
         (intermittent: depends how much imgix's own trim already removed).
         Testing the SCALE is the actual invariant.

         Option 3 (the Lambda-hosted file) has no `w`, so its loaded image IS
         source-sized, the coordinates line up, and it keeps its tight crop.
         Scaled renders already carry `trim=colorUnlessAlpha`, so imgix trims
         what it can server-side; the cost of skipping is transparent padding,
         never lost artwork.
         ⚠ Do NOT narrow this back to a model-param test. Cropping correctly on
         a scaled render needs the TRUE source width to rescale the bbox — see
         the source-dimensions work (task #43). */
      var skipRect = /[?&](?:w|h)=\d/.test(url);
      if (!skipRect && maxX >= 0 && (maxX - minX + 1 !== w || maxY - minY + 1 !== h)) {
        var tw = maxX - minX + 1, th = maxY - minY + 1;
        // Trim transparent margins via imgix `rect` (server-side crop) so the
        // preview stays an imgix CDN URL instead of an inlined base64 data URI.
        // The alpha bbox above is in full-resolution source pixels (fullCanvas
        // is drawn at natural w×h) and `url` is the imgix output
        // (imgixBgRemoveUrl, or the Lambda-reuploaded persistentUrl for
        // the imgix-served models), so rect=x,y,w,h maps 1:1 to the source image.
        displayUrl = url + (url.indexOf('?') === -1 ? '?' : '&') + 'rect=' + minX + ',' + minY + ',' + tw + ',' + th;
      }

      /* SCORING REMOVED (Owen 2026-07-27): the two SCORE_SIZE canvases +
         pixel loops that used to build `mask` (untrimmed alpha) and
         `normalizedMask` (bbox-normalised alpha, for IoU similarity) existed
         ONLY to feed rank(). run() no longer scores, so this work — plus
         rank()'s extra full fetch of the original image — is gone. The trim
         bbox above is still computed: it drives the tight `rect=` crop, which
         is display, not scoring. If scoring is ever restored, restore these
         two canvases with it. */
      return { displayUrl: displayUrl, ok: true };
    }).catch(function (e) {
      console.warn('[bg-removal-multi] loadCandidate failed for', url.slice(0, 80), e.message);
      return { displayUrl: url, ok: false };
    });
  }

  // Main entry. Returns { winner, alternatives, all } resolved when scoring is done.
  // Triggers a 'progress' event so callers can render loading state.
  function run(sourceUrl, imgixBgRemoveUrl, onProgress, onCandidateReady, skipRemote) {
    /* AI-edited sources (2026-08-14, Justin): every NEW bg-removal render of
       an AI-edited image is profile-stripped too — the edit output itself is
       cs=strip'd, so its bg-removed derivatives must match. Detected by the
       v4 re-upload name (<base>-ai-edit.png) or a directly-used Lambda edit
       output (bg-removal-ideogram-edit____*). Covers ix here and pc below;
       gram needs nothing — its Lambda output URL always carries cs=strip. */
    var aiEditedSource = /-ai-edit\.|bg-removal-ideogram-edit____/.test(sourceUrl);
    if (aiEditedSource && imgixBgRemoveUrl && imgixBgRemoveUrl.indexOf('cs=strip') < 0) {
      imgixBgRemoveUrl += (imgixBgRemoveUrl.indexOf('?') >= 0 ? '&' : '?') + 'cs=strip';
    }
    if (ninjaEligible(sourceUrl)) {
      return runNinja(sourceUrl, function (cand) {
        recordBgRemovalVersion(cand);
        if (onCandidateReady) { try { onCandidateReady(cand); } catch (e) {} }
        return cand;
      }).catch(function (e) {
        // Ninja unavailable for this source → the old options, exactly as before.
        console.warn('[bg-removal-multi] Ninja AI failed, using the old options:', e && e.message);
        ninjaFailed[sourceUrl] = true;
        if (onCandidateReady) { try { onCandidateReady({ ninjaFallback: true }); } catch (e2) {} }
        return run(sourceUrl, imgixBgRemoveUrl, onProgress, onCandidateReady, skipRemote);
      });
    }
    var cacheKey = sourceUrl;
    // Memory first, then the cross-reload store (see persistGet). A store hit
    // is promoted into memory so the rest of this session behaves identically
    // to a fresh run's cache.
    var cached = cache.get(cacheKey);
    if (!cached) {
      cached = persistGet(cacheKey);
      if (cached) cache.set(cacheKey, cached);
    }
    if (cached) {
      // Replay onCandidateReady so the UI re-paints from cache the same way
      // it does for a fresh run.
      if (cached.all) {
        cached.all.forEach(function (c) {
          recordBgRemovalVersion(c);
          if (onCandidateReady) { try { onCandidateReady(c); } catch (e) {} }
        });
      }
      return Promise.resolve(cached);
    }

    /* Both local layers missed — try the cross-platform manifest before
       running live. A hit is promoted into memory + localStorage and the
       re-entrant call takes the memory-hit path above (skipRemote breaks the
       recursion on a miss). A miss costs one fast 403/404 and falls through
       to the live run exactly as before. */
    if (!skipRemote) {
      return remoteGet(cacheKey).then(function (remote) {
        if (remote) {
          cache.set(cacheKey, remote);
          persistSet(cacheKey, remote);
        }
        return run(sourceUrl, imgixBgRemoveUrl, onProgress, onCandidateReady, true);
      });
    }

    if (onProgress) onProgress({ phase: 'fetching' });

    // Each candidate is loaded once: we keep its UNTRIMMED alpha mask for
    // scoring (so variance/coverage signals reflect the model's actual
    // bg-removal choices) and a TRIMMED data URL for display (so the user
    // sees tight crops in the preview and Size Guide mockups).
    // persistentUrl is the imgix-served URL of the bg-removed output (for
    // imgix, the bg-remove URL itself; for Gram/PC, the imgix URL
    // returned by Lambda after it re-uploaded the output through the
    // storefront's S3-backed upload pipeline). This is what goes into the
    // cart property — guaranteed CDN-served and persistent.
    // url is the TRIMMED data URL used only for the thumbnail/preview visual.
    // bareUrl is the original (pre-bg-remove) source URL, used as a
    // last-resort fallback if both displayUrl and persistentUrl fail to
    // render in an <img> (e.g., imgix's bg-remove fails for an image type
    // it doesn't support — the bare upload still loads).
    function emitReady(cand) {
      recordBgRemovalVersion(cand);
      if (onCandidateReady) {
        try { onCandidateReady(cand); } catch (e) { /* ignore */ }
      }
      return cand;
    }
    // Model 2 (gram) and Model 3 (pc) sourcing.
    //   - Model 1 (imgix's own bg-remove) is ALWAYS imgix — unchanged.
    //   - Ideogram (gram) is a DIRECT API call via the AWS Lambda proxy on
    //     every template. The Lambda re-hosts its output on the Ninja
    //     S3/imgix stack, so persistentUrl is always an imgix URL.
    //   - Model 3 (pc) is PixelCut via imgix `bg-remove-model=4` on EVERY
    //     template (Owen 2026-07-27). Previously this was gated to the
    //     by-size-middle-scroll A/B body class and fell through to the Lambda
    //     everywhere else — and since zero products remained on that template,
    //     the imgix route had become unreachable and 100% of PixelCut traffic
    //     went through the Lambda. Same vendor either way; imgix keeps it
    //     inside the CDN contract and drops a round-trip + an S3 re-upload.
    //     ⚠ Do NOT re-add a body-class gate here.
    //   - Ideogram (gram) is the DIRECT API via the Lambda proxy, with
    //     `cs=strip` on the produced URL (drops the embedded color profile —
    //     the vendor color-shift suspect; preview, cart, and production all
    //     consume the same stripped pixels). Promoted 2026-08-18 night after
    //     a staging retest. ROUTING LEDGER, because this flipped 4 times:
    //       08-10: imgix model-3 (drop the Lambda round-trip)
    //       08-18 AM: Lambda-direct (Ideogram clarity fix + cs=strip A/B won)
    //         -> CRASHED on large masters: callIdeogram has no size guard,
    //            Ideogram 400s oversize ("unsupported format") / 25s timeout
    //       08-18 PM: back to imgix model-3 (+cs=strip) while diagnosing
    //       08-18 night: Lambda-direct again, BOTH failure modes fixed —
    //         client sends w=2000&fm=png (large masters), and the Lambda's
    //         fetchBytes retries the ~1s ephemeral-URL 404 race (Owen's fix;
    //         busy/photographic art used to lose that race and 502).
    //     imgixModelCandidate('gram', 3) is the documented fallback — imgix
    //     models have gone down before too (models 2/4 outage 2026-07-01).
    //   ⚠ imgix's `bg-remove-model=2` slot is a RETIRED vendor we
    //     deliberately moved off — do NOT route anything through model 2.

    // imgix model-variant URL: built straight off the ORIGINAL source image
    // (sourceUrl is the bare imgix upload, query stripped) using the site's
    // high-res convention (trim=colorUnlessAlpha&fm=png&w=2000&bg-remove=true)
    // plus the model selector — pc => bg-remove-model=4 (model 2 is retired).
    // This URL IS the persistent, CDN-served output, so it doubles as the cart
    // property value (no AWS Lambda re-upload needed).
    function imgixModelCandidate(name, modelNum) {
      /* cs=strip: ALWAYS on gram (option 3 — Ideogram outputs carry the
         color-shift-suspect profile), and on any model when the SOURCE is an
         AI-edited image (its pixels are already profile-stripped, so the
         derivatives must match). Justin 2026-08-14. */
      var url = sourceUrl +
        '?trim=colorUnlessAlpha&fm=png&w=2000&bg-remove=true&bg-remove-model=' + modelNum +
        ((name === 'gram' || aiEditedSource) ? '&cs=strip' : '');
      return loadCandidate(url).then(function (d) {
        return emitReady({ name: name, url: d.displayUrl, persistentUrl: url, bareUrl: sourceUrl, ok: d.ok });
      });
    }
    function lambdaCandidate(name) {
      /* Send the print-safe downscale (w=2000&fm=png), NOT the bare original
         (2026-08-14, large-image crash fix): the Lambda's bg-removal path has
         no size guard — Ideogram 400s on oversize masters (misleading
         "unsupported format", observed at 14.5MB) and big uploads can blow
         its 25s upstream timeout. This is the same input class Ideogram
         received in the imgix model-3 era (that URL baked in w=2000&fm=png)
         and matches the ix/pc render convention. The Lambda's EDIT path
         already downscale-retries; bg-removal doesn't, so the client sends
         it pre-shrunk. */
      var lambdaSource = sourceUrl +
        (sourceUrl.indexOf('?') >= 0 ? '&' : '?') + 'w=2000&fm=png';
      // One Lambda retry (same transient class as the image loads: the
      // proxy's own fetch of a cold source can fail once during ingest lag).
      return callBgRemoval(name, lambdaSource).catch(function (e) {
        console.warn('[bg-removal-multi] ' + name + ' lambda retry in 3s:', e && e.message);
        return new Promise(function (res) { setTimeout(res, 3000); }).then(function () {
          return callBgRemoval(name, lambdaSource);
        });
      }).then(function (persistentUrl) {
        /* cs=strip (2026-08-14, Justin): strip the embedded color profile
           from the produced render — it rides the persistent URL, so the
           preview, the cart property, and production all consume the same
           profile-stripped pixels (the vendor color-shift suspect).
           Downstream appends are separator-aware. */
        persistentUrl += (persistentUrl.indexOf('?') >= 0 ? '&' : '?') + 'cs=strip';
        /* ONE-FOR-ONE PREVIEW (Owen 2026-08-01): display the SAME render
           production prints — hosted PNG + trim=colorUnlessAlpha&trim-tol=10
           (tol required: Ideogram margins carry alpha noise 2-4/255, zero-tol
           trim no-ops). The client rect crop becomes a no-op on the already-
           trimmed render. gram-only: this function has one caller ('gram');
           ix/pc previews are built elsewhere and untouched. persistentUrl
           stays BARE — the cart hook appends the same trim params for
           production, so preview and print are the same pixels. */
        var previewUrl = persistentUrl +
          (persistentUrl.indexOf('?') >= 0 ? '&' : '?') +
          'trim=colorUnlessAlpha&trim-tol=10';
        return loadCandidate(previewUrl).then(function (d) {
          return emitReady({ name: name, url: d.displayUrl, persistentUrl: persistentUrl, bareUrl: sourceUrl, ok: d.ok });
        });
      });
    }

    var promises = [
      loadCandidate(imgixBgRemoveUrl).then(function (d) {
        return emitReady({
          name: 'ix',
          url: d.displayUrl,
          persistentUrl: imgixBgRemoveUrl,
          bareUrl: sourceUrl,
          ok: d.ok
        });
      }),
      imgixModelCandidate('pc', 4),
      /* gram = direct Ideogram API via the Lambda (Justin, promoted 2026-08-18
         night after the staging retest; see the routing note above). Both
         prior failure modes are covered: the client sends the w=2000&fm=png
         downscale, and the Lambda's fetchBytes retries the ephemeral-URL 404
         race. Fallback if the Lambda/vendor misbehaves:
         imgixModelCandidate('gram', 3). */
      lambdaCandidate('gram')
    ];
    /* ⚠ ALL THREE ALWAYS RUN — BUSINESS DECISION, NOT AN OVERSIGHT (Owen
       2026-07-27: "non-negotiable"). Deferring Options 2/3 until the customer
       opens the picker would remove a Lambda call + an imgix model render from
       most uploads, and it has been costed out — the answer is no: the three
       options are shown up front on purpose. Do NOT lazy-load, gate behind a
       "compare" affordance, or run them on scroll-into-view. */

    // Use allSettled so a single model failure doesn't kill the rest.
    return Promise.all(promises.map(function (p) {
      return p.then(
        function (v) { return { status: 'fulfilled', value: v }; },
        function (e) { return { status: 'rejected', reason: e }; }
      );
    })).then(function (settled) {
      var candidates = [];
      settled.forEach(function (s) {
        if (s.status === 'fulfilled') candidates.push(s.value);
        else console.warn('[bg-removal-multi] candidate failed:', s.reason && s.reason.message);
      });
      if (candidates.length === 0) throw new Error('All bg-removal candidates failed');

      /* NO SCORING + FIXED SLOTS (Owen 2026-07-27).
         Was: rank() computed an alpha-mask quality score per candidate and
         sorted slots 2/3 by it, plus an ≥80%-similarity dedupe. That cost an
         EXTRA full fetch of the original image (getOriginalRGBA) and two
         256x256 canvases + a pixel loop per candidate — and the dedupe result
         was already thrown away, since both engines render `all`, not
         `filtered` (the filter used to collapse the picker to one option).
         Now: slots are pinned imgix -> PixelCut -> Ideogram, so the customer
         sees the same vendor in the same position every time.
         ⚠ The score also silently DROPPED candidates whose image never
         decoded (score === Infinity). loadCandidate resolves rather than
         rejects on failure, so without that filter a dead model would render
         as a blank option card. `ok !== false` below replaces it at zero
         cost — do not remove it. */
      var SLOT_ORDER = ['ix', 'pc', 'gram'];
      var ordered = candidates
        .filter(function (c) { return c.ok !== false; })
        .sort(function (a, b) {
          return SLOT_ORDER.indexOf(a.name) - SLOT_ORDER.indexOf(b.name);
        });
      if (ordered.length === 0) throw new Error('All bg-removal candidates failed to decode');
      var result = {
        winner: ordered[0],
        alternatives: ordered.slice(1),
        all: ordered
      };
      cache.set(cacheKey, result);
      // PARTIAL results must not stick for the session (Owen 2026-07-27):
      // if a model still failed after the retries, evict this cache entry
      // after 60s so the NEXT toggle/open re-runs fresh instead of replaying
      // a 1-option picker forever. Complete runs stay cached as before.
      if (ordered.length < promises.length) {
        setTimeout(function () {
          if (cache.get(cacheKey) === result) cache.delete(cacheKey);
        }, 60000);
      } else {
        // COMPLETE runs only — a partial must never survive a reload either.
        persistSet(cacheKey, result);
        remotePut(cacheKey, result); // fire-and-forget cross-platform copy
      }
      return result;
    });
  }

  function clearCache() {
    cache.clear();
    // Clear OUR persisted entries too, or clearCache() would leave the
    // cross-reload store authoritative and the "clear" would be a no-op on
    // the next run.
    try {
      var mine = [];
      for (var i = 0; i < sessionStorage.length; i++) {
        var k = sessionStorage.key(i);
        if (k && k.indexOf(PERSIST_V) === 0) mine.push(k);
      }
      mine.forEach(function (k) { sessionStorage.removeItem(k); });
    } catch (e) { /* nothing to clear */ }
  }

  /* ⚠ BSV4 ARM — DO NOT DROP when rebuilding this file from a local copy
     (the studio-recolor v4 arm was clobbered once already, 2026-07-10). The
     by-size-v4 bundle drives THIS same candidate pipeline (imgix + PixelCut
     via imgix + Ideogram via the Lambda proxy, fixed slot order) through this
     handle, so both templates always show the same options in the same
     sequence. Defining a global
     has zero effect on any legacy path. */
  window.__bsv4BgrRun = function () {
    var provider = window.NtBgrStockProvider;
    if (provider && provider.active() && arguments[5] && arguments[5].file) return provider.run.apply(null, arguments);
    return run.apply(null, arguments);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Tab UI
  // ─────────────────────────────────────────────────────────────────────────

  // Picker CSS now ships from assets/nt-bgr-picker.css (loaded by
  // layout/theme.liquid alongside this script, gated to template_suffix
  // 'by-size-bg-options'). This function used to string-build a <style>
  // tag with ~450 lines of inline CSS — now a no-op so any call site
  // that still invokes it remains safe.
  function ensureStyles() { /* no-op — see assets/nt-bgr-picker.css */ }


  // Find or create the tabs container for a given upload block. The block is
  // the wrapper that contains both the preview img and the toggle-options
  // (.bgRemover). The tabs container is inserted in its OWN row above the
  // options row (toggles + Edit with AI), not as a sibling flex item — that
  // way it gets full width and the buttons don't get squished into letters.
  function getOrCreateTabsContainer(block) {
    block = block || document;
    var scope = block.querySelector ? block : document;
    var existing = scope.querySelector('.nt-bgr-tabs');
    if (existing) return existing;
    // Try the newer template first (toggle-options), then the legacy one.
    var anchor = scope.querySelector('toggle-options') ||
                 scope.querySelector('.fileupload_bg_options');
    // ── Legacy-markup fallback ──────────────────────────────────────────
    // On older products (spangle, puff, gangsheet, custom magnets/keychains,
    // etc.) the toggle (`.fileupload_bg_options` / `#option_checkbox`) is a
    // SIBLING of the upload preview block, not a descendant. When the
    // scoped lookup fails, walk UP the DOM from the block until we find an
    // ancestor that contains the anchor. CRITICAL: at each ancestor, also
    // check for an EXISTING .nt-bgr-tabs in that scope — otherwise we'd
    // re-create the picker on every polling tick (the block-scoped existing
    // check at the top can't see pickers inserted via walk-up).
    if (!anchor && block && block.parentElement) {
      var cur = block.parentElement;
      while (cur && cur !== document.body) {
        existing = cur.querySelector('.nt-bgr-tabs');
        if (existing) return existing;
        anchor = cur.querySelector('toggle-options') ||
                 cur.querySelector('.fileupload_bg_options');
        if (anchor) break;
        cur = cur.parentElement;
      }
    }
    if (!anchor) {
      // Last-resort document-wide search. Re-check for existing picker first.
      existing = document.querySelector('.nt-bgr-tabs');
      if (existing) return existing;
      anchor = document.querySelector('toggle-options') ||
               document.querySelector('.fileupload_bg_options');
    }
    if (!anchor) return null;
    // Final dedupe: if a picker already exists ANYWHERE adjacent to the
    // anchor we just found, reuse it. Guards against multi-upload pages
    // that have several anchors but should only show one picker per upload.
    var anchorParent = anchor.parentElement && anchor.parentElement.parentElement;
    if (anchorParent) {
      existing = anchorParent.querySelector('.nt-bgr-tabs');
      if (existing) return existing;
    }
    var el = document.createElement('div');
    el.className = 'nt-bgr-tabs';
    // Insert the picker as a SIBLING immediately BEFORE the toggle row so it
    // appears ABOVE the AI toolbar (unified card: gray picker on top, white
    // toolbar below). Falls back to inserting before the anchor if the DOM
    // doesn't have the expected nesting.
    var optionsRow = anchor.parentElement;
    if (optionsRow && optionsRow.parentElement) {
      optionsRow.parentElement.insertBefore(el, optionsRow);
    } else {
      anchor.parentNode.insertBefore(el, anchor);
    }
    return el;
  }

  function showLoading(block) {
    ensureStyles();
    var container = getOrCreateTabsContainer(block);
    if (!container) return;
    container.innerHTML = '<span class="nt-bgr-loading">Generating Background Removal Options</span>';
    container.classList.add('is-visible');
  }

  function hide(block) {
    var scope = block && block.querySelector ? block : document;
    var containers = scope.querySelectorAll('.nt-bgr-tabs');
    containers.forEach(function (c) {
      c.classList.remove('is-visible');
      c.innerHTML = '';
    });
  }

  // If the block's Remove Background toggle is OFF, any (re)render of the
  // picker must come up FULLY HIDDEN — not minimized. (Owen 2026-06-23: the
  // minimized "NEW · 3 BG Removal options · Show" bar should never be visible
  // while Remove Background is off, on any preview surface.) Without this, the
  // async re-render paths (candidates resolving via updateScaffoldSlot →
  // renderChooserButtons, resaves, perfect-match arrivals) rebuild the
  // container visible and silently re-show it after the change-listener hid it
  // at toggle-off. Called at the end of every render path.
  function applyToggleCollapsedState(container) {
    try {
      if (!container || !container.closest) return;
      var blk = container.closest('uploaded-files-block') ||
                container.closest('upload-controls') ||
                container.closest('file-preview') ||
                document;
      var tog = (blk.querySelector ? blk : document).querySelector('.bgRemover .toggleOption, #option_checkbox, #bgRemover, #bgRemover_');
      if (tog && !tog.checked) {
        // Remove Background OFF → hide the picker entirely (remove is-visible).
        container.classList.remove('is-visible');
      }
    } catch (e) { /* never break a render */ }
  }

  // Build the full chooser UI (header + V1/V2/V3 buttons) for a given list
  // of candidates. Extracted so we can call it from both the normal path
  // (visible alternates) and the "See other results" expand path.
  //
  // opts:
  //   bestMatchName  — when set, the candidate with this `name` gets a green
  //                    checkmark badge on its thumb (regardless of which
  //                    version is the active selection). Used in the
  //                    perfect-match-expanded path so the customer can see
  //                    which one was deemed best.
  //   onHide         — when set, render a "Hide" button next to the header
  //                    that calls this callback. Used in the perfect-match-
  //                    expanded path to collapse back to the badge + link.
  function renderChooserButtons(container, candidates, onSelect, opts) {
    opts = opts || {};
    container.innerHTML = '';

    var headerRow = document.createElement('div');
    headerRow.className = 'nt-bgr-tabs__header-row';
    headerRow.appendChild(buildChooserHeader());
    // Always-present Hide/Show toggle. If the caller passed onHide (the
    // perfect-match-expanded path) it destroys the chooser; otherwise the
    // button toggles .is-collapsed in place.
    headerRow.appendChild(buildHideToggle(container, opts.onHide));
    container.appendChild(headerRow);

    var btnRow = document.createElement('div');
    btnRow.className = 'nt-bgr-tabs__buttons';
    container.appendChild(btnRow);

    // Decide which version is the active selection. Priority:
    //   1. opts.activeName  — caller\'s explicit choice (preserves V2/V3
    //      selection across Hide → See other results toggles).
    //   2. First candidate  — default for the initial render.
    var activeName = opts.activeName || (candidates[0] && candidates[0].name);

    candidates.forEach(function (cand, idx) {
      var btn = document.createElement('button');
      btn.type = 'button';
      var isActive = cand.name === activeName;
      btn.className = 'nt-bgr-tab' + (isActive ? ' is-active' : '');
      btn.setAttribute('data-name', cand.name);
      // No title attribute — customers don\'t need to see the underlying
      // model name (ix / gram / pc) on hover.

      var thumbWrap = document.createElement('span');
      thumbWrap.className = 'nt-bgr-tab__thumb-wrap';

      var thumb = document.createElement('img');
      thumb.className = 'nt-bgr-tab__thumb';
      thumb.alt = 'Option ' + (idx + 1);
      thumb.loading = 'lazy';
      // Fallback chain: trimmed display URL → persistent (bg-remove) URL →
      // bare upload URL (last resort so V1 never renders a broken icon when
      // imgix\'s bg-removal endpoint fails — this only kicks in for imgix
      // because Gram/PC go through Lambda which already re-hosts
      // their outputs to imgix CDN).
      var fallbacks = [cand.url, cand.persistentUrl, cand.bareUrl].filter(function (u) {
        return u && typeof u === 'string';
      });
      var fbIdx = 0;
      thumb.onerror = function () {
        fbIdx++;
        while (fbIdx < fallbacks.length && fallbacks[fbIdx] === fallbacks[fbIdx - 1]) fbIdx++;
        if (fbIdx < fallbacks.length) {
          console.warn('[bg-removal-multi]', cand.name, 'thumb failed; trying fallback', fbIdx);
          thumb.src = fallbacks[fbIdx];
        } else {
          thumb.onerror = null;
        }
      };
      thumb.src = fallbacks[0] || '';
      thumbWrap.appendChild(thumb);
      var versionLabel = document.createElement('span');
      versionLabel.className = 'nt-bgr-tab__version-label';
      versionLabel.setAttribute('aria-hidden', 'true');
      versionLabel.textContent = 'v' + (idx + 1);
      thumbWrap.appendChild(versionLabel);
      btn.appendChild(thumbWrap);

      var label = document.createElement('span');
      label.className = 'nt-bgr-tab__label';
      label.textContent = 'Option ' + (idx + 1);
      btn.appendChild(label);

      // "Recommended" subtitle on the best-match candidate (always shown when
      // we know the best match, regardless of whether the customer has
      // currently selected this version). Replaces the corner check badge.
      if (opts.bestMatchName && cand.name === opts.bestMatchName) {
        var rec = document.createElement('span');
        rec.className = 'nt-bgr-tab__recommended';
        rec.textContent = 'Recommended';
        btn.appendChild(rec);
      }

      btn.addEventListener('click', function () {
        btnRow.querySelectorAll('.nt-bgr-tab').forEach(function (t) {
          t.classList.remove('is-active');
        });
        btn.classList.add('is-active');
        if (onSelect) onSelect(cand);
      });
      btnRow.appendChild(btn);
    });
    container.classList.add('is-visible');
    applyToggleCollapsedState(container);
  }

  // Single-line header: dark-blue "NEW" badge + copy explaining the chooser.
  // Short variant kept in the DOM as an a11y fallback; CSS hides it.
  function buildChooserHeader() {
    var header = document.createElement('div');
    header.className = 'nt-bgr-tabs__header';

    var line1 = document.createElement('div');
    line1.className = 'nt-bgr-tabs__header-line nt-bgr-tabs__header-line--lead';
    line1.innerHTML =
      '<span class="nt-bgr-tabs__header-badge" aria-hidden="true">NEW</span>' +
      '<span class="nt-bgr-tabs__header-text nt-bgr-tabs__header-text--full">3 Background Removal options to choose from.</span>' +
      '<span class="nt-bgr-tabs__header-text nt-bgr-tabs__header-text--short">3 BG Removal options</span>';
    header.appendChild(line1);

    return header;
  }

  // Hide/Show toggle: text + chevron. Click toggles .is-collapsed on the
  // container (in-place collapse). If `onHideCallback` is provided (used by
  // the perfect-match path that wants full destruction), the callback fires
  // instead of the in-place toggle.
  function buildHideToggle(container, onHideCallback) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'nt-bgr-hide';
    btn.setAttribute('aria-expanded', 'true');
    btn.innerHTML =
      '<span class="nt-bgr-hide__text">Hide</span>' +
      '<span class="nt-bgr-hide__chevron" aria-hidden="true">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="18 15 12 9 6 15"></polyline></svg>' +
      '</span>';
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      if (typeof onHideCallback === 'function') {
        onHideCallback();
        return;
      }
      var collapsed = container.classList.toggle('is-collapsed');
      btn.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      var textEl = btn.querySelector('.nt-bgr-hide__text');
      if (textEl) textEl.textContent = collapsed ? 'Show' : 'Hide';
    });
    return btn;
  }

  // Render the chooser scaffold IMMEDIATELY — Option 1 (imgix) is populated
  // with the live preview URL, Options 2 & 3 show loading spinners until
  // their candidates resolve. Customer can see Option 1 and even pick it
  // while gram/pc finish in the background.
  //
  // Order is always ix → pc → gram so slots match the labels even
  // when a candidate fails (it just stays in its loading state — the
  // updateScaffoldSlot call never fires for a failed candidate).
  function renderScaffold(container, imgixUrl, bareUrl, onSelect) {
    ensureStyles();
    container.innerHTML = '';
    container.classList.add('is-visible');

    var headerRow = document.createElement('div');
    headerRow.className = 'nt-bgr-tabs__header-row';
    headerRow.appendChild(buildChooserHeader());
    headerRow.appendChild(buildHideToggle(container));
    container.appendChild(headerRow);

    var btnRow = document.createElement('div');
    btnRow.className = 'nt-bgr-tabs__buttons';
    container.appendChild(btnRow);

    // Stub imgix candidate so the slot is selectable immediately. Real
    // candidate (with mask + trimmed displayUrl) replaces this via
    // updateScaffoldSlot when loadCandidate resolves.
    var imgixStub = {
      name: 'ix',
      url: imgixUrl,
      persistentUrl: imgixUrl,
      bareUrl: bareUrl,
      ok: true
    };

    // Ninja AI: three ranked slots, no imgix stub (Rank 1 is applied when it lands).
    var ninjaMode = ninjaEligible(bareUrl);
    var slotOrder = ninjaMode ? ['n1', 'n2', 'n3'] : ['ix', 'pc', 'gram'];
    if (ninjaMode) imgixStub = null;
    slotOrder.forEach(function (name, idx) {
      var slot = document.createElement('button');
      slot.type = 'button';
      slot.className = 'nt-bgr-tab' + (idx === 0 ? ' is-active' : '');
      slot.setAttribute('data-name', name);

      var thumbWrap = document.createElement('span');
      thumbWrap.className = 'nt-bgr-tab__thumb-wrap';

      // All three slots start in the loading state — Option 1 also shows
      // the spinner briefly so the chooser looks consistent while every
      // option resolves. The imgix slot still gets its stub candidate
      // attached so an early click (after is-loading is removed by
      // updateScaffoldSlot) still works.
      slot.classList.add('is-loading');
      var ph = document.createElement('span');
      ph.className = 'nt-bgr-tab__thumb nt-bgr-tab__thumb--loading';
      var spin = document.createElement('span');
      spin.className = 'nt-bgr-tab__spinner';
      spin.innerHTML = '<svg viewBox="25 25 50 50"><circle cx="50" cy="50" r="20" fill="none" stroke-width="4"></circle></svg>';
      ph.appendChild(spin);
      thumbWrap.appendChild(ph);
      var versionLabel = document.createElement('span');
      versionLabel.className = 'nt-bgr-tab__version-label';
      versionLabel.setAttribute('aria-hidden', 'true');
      versionLabel.textContent = 'v' + (idx + 1);
      thumbWrap.appendChild(versionLabel);
      if (idx === 0) slot.__ntCand = imgixStub;
      slot.appendChild(thumbWrap);

      var label = document.createElement('span');
      label.className = 'nt-bgr-tab__label';
      label.textContent = 'Option ' + (idx + 1);
      slot.appendChild(label);

      slot.addEventListener('click', function () {
        if (slot.classList.contains('is-loading')) return;
        btnRow.querySelectorAll('.nt-bgr-tab').forEach(function (t) {
          t.classList.remove('is-active');
        });
        slot.classList.add('is-active');
        var cand = slot.__ntCand;
        if (cand && onSelect) onSelect(cand);
      });
      btnRow.appendChild(slot);
    });
    applyToggleCollapsedState(container);
  }

  // Replace a scaffold slot\'s loading spinner with the resolved candidate\'s
  // thumb image. Called from the run() per-candidate-ready callback.
  function updateScaffoldSlot(container, cand) {
    if (!container || !cand) return;
    var slot = container.querySelector('.nt-bgr-tab[data-name="' + cand.name + '"]');
    if (!slot) return;
    slot.__ntCand = cand;
    var thumbWrap = slot.querySelector('.nt-bgr-tab__thumb-wrap');
    if (!thumbWrap) return;
    thumbWrap.innerHTML = '';
    var thumb = document.createElement('img');
    thumb.className = 'nt-bgr-tab__thumb';
    thumb.alt = (slot.querySelector('.nt-bgr-tab__label') || {}).textContent || cand.name;
    thumb.loading = 'lazy';
    var fallbacks = [cand.url, cand.persistentUrl, cand.bareUrl].filter(function (u) {
      return u && typeof u === 'string';
    });
    var fbIdx = 0;
    thumb.onerror = function () {
      fbIdx++;
      while (fbIdx < fallbacks.length && fallbacks[fbIdx] === fallbacks[fbIdx - 1]) fbIdx++;
      if (fbIdx < fallbacks.length) thumb.src = fallbacks[fbIdx];
      else thumb.onerror = null;
    };
    thumb.src = fallbacks[0] || '';
    thumbWrap.appendChild(thumb);
    // Re-add the v1/v2/v3 corner label (innerHTML wipe above removed it).
    var slotIdx = Array.prototype.indexOf.call(
      slot.parentNode ? slot.parentNode.children : [],
      slot
    );
    if (slotIdx >= 0) {
      var versionLabel = document.createElement('span');
      versionLabel.className = 'nt-bgr-tab__version-label';
      versionLabel.setAttribute('aria-hidden', 'true');
      versionLabel.textContent = 'v' + (slotIdx + 1);
      thumbWrap.appendChild(versionLabel);
    }
    slot.classList.remove('is-loading');
  }

  // Render the version chooser given the result. The "winner" is always imgix
  // (per the north-star rule); `alternatives` are alts <80% similar to imgix.
  //
  // Three paths:
  //   - 2+ visible candidates: render the full chooser immediately.
  //   - 1 visible + extras filtered as near-duplicates (perfect match):
  //     show a "See other results" link that expands to ALL candidates
  //     (result.all) when clicked.
  //   - Only 1 candidate total (rare — usually means models all failed):
  //     clear the container silently.
  function renderTabs(result, onSelect, block) {
    ensureStyles();
    var container = getOrCreateTabsContainer(block);
    if (!container) return;

    // Always show every successful candidate (ix, gram, pc) as
    // V1/V2/V3. No more "Recommended" / "See other results" collapse — the
    // customer picks from the full set every time.
    var allCandidates = (result.all && result.all.length)
      ? result.all
      : [result.winner].concat(result.alternatives);

    // Imgix is the default selection; record it for the cart-add interceptor.
    setBgRemovalProperty(allCandidates[0]);

    renderChooserButtons(container, allCandidates, onSelect);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Public API
  // ─────────────────────────────────────────────────────────────────────────

  window.NinjaBgRemoval = {
    run: run,
    showLoading: showLoading,
    hide: hide,
    renderTabs: renderTabs,
    clearCache: clearCache
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Auto-init: watch the upload input + Remove Background toggle and trigger
  // the multi-model run whenever a new image is uploaded or the toggle is
  // flipped on. Independent of which existing upload code path is active
  // (Popular vs. Custom size, etc.).
  // ─────────────────────────────────────────────────────────────────────────

  // Find all preview images on the page. The selector covers:
  //  - legacy: <file-preview> #fileupload_hero (id) and .viewer-box img
  //  - new (bySize_popular):
  //      upload-controls file-preview preview-block .fileupload_hero
  //      multi-upload uploaded-files-block file-preview preview-block .fileupload_hero
  function findPreviewImgs() {
    var nodes = document.querySelectorAll(
      '.fileupload_hero, #fileupload_hero, file-preview .viewer-box img'
    );
    // Filter to <img> elements (xzoom containers also use .fileupload_hero on
    // non-img wrappers).
    return Array.from(nodes).filter(function (n) {
      return n.tagName === 'IMG';
    });
  }

  // The theme renders a native "Preview: ..." caption (.previewText) directly
  // BELOW the uploaded-artwork preview. On this template we want it ABOVE the
  // preview image, so relocate the native element above its <file-preview>
  // sibling. Idempotent — only moves when it isn't already in place.
  function movePreviewNoteAboveImage() {
    document.querySelectorAll('file-preview').forEach(function (fp) {
      var host = fp.parentNode;
      if (!host) return;
      var note = null, sib = fp.nextElementSibling;
      while (sib) {
        if (sib.tagName === 'DIV' && sib.classList.contains('previewText')) { note = sib; break; }
        sib = sib.nextElementSibling;
      }
      if (note) host.insertBefore(note, fp);
    });
  }

  // Module-level so applyChoice() can mark bareUrls as "already applied —
  // don't treat it as a new upload" alongside the polling's own tracking.
  var appliedBareUrls = new WeakMap(); // img -> Set of bareUrls applyChoice set

  // Helpers to put a loading overlay on the preview area for the duration of
  // a multi-model run. Without this the customer sees the imgix-only
  // bg-remove output (set by bySize_popular) for 2-5s before our final
  // selection is applied — confusing transient state.
  function getPreviewWrappers(block) {
    var scope = block && block.querySelectorAll ? block : document;
    // Apply the loading state to whichever of these is the closest visible
    // wrapper around the preview image. Try in order of specificity. The
    // first one with non-zero dimensions wins. Falls back to file-preview
    // (always there) so SOMETHING gets the loading state.
    var selectors = [
      '.viewer-box',
      'preview-block',
      'preview-box',
      'file-preview'
    ];
    var found = null;
    for (var i = 0; i < selectors.length; i++) {
      var nodes = scope.querySelectorAll(selectors[i]);
      for (var j = 0; j < nodes.length; j++) {
        var n = nodes[j];
        var r = n.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) {
          found = n;
          break;
        }
        // Even 0-dim file-preview is acceptable as a last resort, since the
        // loading style forces min-height/min-width.
        if (!found && selectors[i] === 'file-preview') found = n;
      }
      if (found && found.getBoundingClientRect().width > 0) break;
    }
    return found ? [found] : (block ? [block] : []);
  }
  function setPreviewLoading(block, on) {
    getPreviewWrappers(block).forEach(function (w) {
      if (on) {
        w.classList.add('nt-bgr-preview-loading');
        if (!w.querySelector(':scope > .nt-bgr-spinner-svg')) {
          var spinner = document.createElement('div');
          spinner.className = 'nt-bgr-spinner-svg';
          spinner.innerHTML = '<svg viewBox="25 25 50 50"><circle cx="50" cy="50" r="20" fill="none" stroke-width="4"></circle></svg>';
          w.appendChild(spinner);
        }
      } else {
        w.classList.remove('nt-bgr-preview-loading');
        var spinner = w.querySelector(':scope > .nt-bgr-spinner-svg');
        if (spinner) spinner.remove();
      }
    });
  }

  // Apply the chosen URL to every preview image associated with this block
  // (or the whole page if no block context). Covers:
  //  - the main image preview (.fileupload_hero / #fileupload_hero / .viewer-box img)
  //  - the Ninja Size Guide mockups (#dtf_body_image .watermark_image img)
  //
  // Also records the chosen URL's bareUrl in `appliedBareUrls` so the polling
  // doesn't see this src change as a "new upload" and kick off another
  // multi-model run — important when the chosen URL has a different bareUrl
  // than the original source (e.g., V2/V3 outputs at fresh imgix paths
  // returned by the Lambda, or the no-trim case where displayUrl is the raw
  // imgix URL rather than a data URL).
  function applyChoice(chosenUrl, block) {
    var scope = block && block.querySelectorAll ? block : document;
    var imgs = scope.querySelectorAll(
      '.fileupload_hero, #fileupload_hero, .viewer-box > img, .viewer-box img'
    );
    var bareOfChosen = (typeof chosenUrl === 'string' && !/^data:/.test(chosenUrl))
      ? chosenUrl.split('?')[0]
      : null;
    // Track when ALL main-preview imgs finish loading the new src so we can
    // remove the loading overlay only after the new image is actually
    // visible. Wait at most 4s so a stuck load doesn't leave the spinner
    // running forever.
    var mainImgs = [];
    imgs.forEach(function (img) {
      if (img.tagName !== 'IMG') return;
      if (img.closest && img.closest('#dtf_body_image')) return; // size guide doesn't gate the loading state
      mainImgs.push(img);
      img.setAttribute('src', chosenUrl);
      if (bareOfChosen) {
        var seen = appliedBareUrls.get(img);
        if (!seen) { seen = new Set(); appliedBareUrls.set(img, seen); }
        seen.add(bareOfChosen);
      }
    });
    // Size Guide mockups live OUTSIDE the upload-controls block, so always
    // target them at document scope. (No polling watches these.)
    var sizeGuideImgs = document.querySelectorAll('#dtf_body_image .watermark_image img');
    sizeGuideImgs.forEach(function (img) {
      if (img.tagName === 'IMG') img.setAttribute('src', chosenUrl);
    });

    // Remove the preview loading overlay once the new src has rendered on
    // every visible main-preview img. For data URLs the load is synchronous
    // and `complete` is already true; for http(s) URLs we wait for the
    // `load` event. Hard timeout keeps the UI from getting stuck.
    if (mainImgs.length === 0) {
      setPreviewLoading(block, false);
      return;
    }
    var remaining = mainImgs.length;
    var done = false;
    function finish() {
      if (done) return;
      done = true;
      setPreviewLoading(block, false);
    }
    mainImgs.forEach(function (img) {
      var settle = function () {
        remaining--;
        if (remaining <= 0) finish();
        img.removeEventListener('load', settle);
        img.removeEventListener('error', settle);
      };
      if (img.complete && img.naturalWidth > 0) {
        // Already loaded (data URL or browser cache)
        settle();
      } else {
        img.addEventListener('load', settle);
        img.addEventListener('error', settle);
      }
    });
    setTimeout(finish, 4000);
  }

  // Force every candidate URL into the browser cache before we render any
  // <img> tags pointing at them. Once cached, the actual render is a synchronous
  // memory hit — so the user never sees a half-loaded or fallback-to-wrong
  // image flash through the thumbnails or the main preview.
  function preloadCandidateImages(candidates) {
    return Promise.all(candidates.map(function (c) {
      return new Promise(function (resolve) {
        var url = c && c.url;
        if (!url) { resolve(); return; }
        if (url.indexOf('data:') === 0) { resolve(); return; } // data URLs are instant
        var img = new Image();
        var done = false;
        function finish() { if (!done) { done = true; resolve(); } }
        img.onload = finish;
        img.onerror = finish;
        img.src = url;
        setTimeout(finish, 6000); // hard ceiling so a stuck URL doesn\'t hang the UI
      });
    }));
  }

  // ── Single-upload dims sync on option pick (Justin 7-13) ─────────────────
  // Picking Option 1/2/3 swaps the PREVIEW to that candidate (raster results
  // arrive trimmed to content), but nothing re-derived the size math: the
  // W/H inputs, fileDetails ratios and price all kept the previous image's
  // geometry (moon photo: hero became a 442x442 cutout while the inputs
  // still said 4.00x2.74 from the 1200x821 original — reads as a "weird
  // zoom"). Multi upload / v2 builder blocks re-run the engine per block and
  // don't have this gap, so this covers SINGLE upload only, reusing the
  // engine's own recalc idiom from applyImgToPreviewBox (bySize_popular.js /
  // pdp_transferBySize.js define identical globals): measure the visual URL
  // (minus display w/h caps), rewrite fileDetails, clamp, applySizes,
  // calculatePrices, ratio refresh. Guarded so surfaces without the engine
  // (blanks etc.) are untouched.
  var dimsSyncSeq = 0;
  var bareDimsCache = {};
  function stripQuery(u) { return String(u || '').split('?')[0]; }
  function stripWH(u) {
    return String(u || '').replace(/([?&])(w|h)=[^&]*/g, '$1').replace(/[?&]+$/, '').replace(/([?&])&+/g, '$1');
  }
  function syncSingleUploadDims(chosen, visualUrl) {
    try {
      if (typeof uploadType === 'undefined' || uploadType !== 'single') return;
      if (typeof getImageSizeFromUrl !== 'function' ||
          typeof applySizes !== 'function' ||
          typeof calculatePrices !== 'function' ||
          typeof getMatchingVariant !== 'function' ||
          !window.jQuery) return;
    } catch (_) { return; }
    var $ = window.jQuery;
    var fdEl = $('upload-controls file-preview file-details');
    if (!fdEl.length || !fdEl.text()) return;
    var seq = ++dimsSyncSeq;

    // Physical size model: the imgix candidate is source-resolution, so its
    // trimmed pixels /300 are the truth (same math the engine runs on
    // toggle-on). The AI candidates (gram/pc) come back DOWNSCALED
    // — raw pixels /300 collapsed a 4in artwork to 0.5in (Justin 7-13) — so
    // for those, physical size = original upload's inches x the content
    // fraction of the candidate's own frame (rect bbox / full frame).
    var dimsPromise;
    if (chosen && chosen.name !== 'ix' && chosen.persistentUrl && chosen.bareUrl) {
      var bareKey = stripQuery(chosen.bareUrl);
      if (!bareDimsCache[bareKey]) bareDimsCache[bareKey] = getImageSizeFromUrl(stripWH(chosen.bareUrl));
      dimsPromise = Promise.all([
        getImageSizeFromUrl(stripWH(chosen.url && chosen.url.indexOf('http') === 0 ? chosen.url : visualUrl)),
        getImageSizeFromUrl(stripQuery(chosen.persistentUrl)),
        bareDimsCache[bareKey]
      ]).then(function (r) {
        var content = r[0], frame = r[1], bare = r[2];
        if (!content.width || !frame.width || !bare.width) throw new Error('measure failed');
        return {
          width: Math.max(1, Math.round(bare.width * content.width / frame.width)),
          height: Math.max(1, Math.round(bare.height * content.height / frame.height))
        };
      });
    } else {
      dimsPromise = getImageSizeFromUrl(stripWH(visualUrl));
    }

    dimsPromise.then(function (dims) {
      if (!dims || !dims.width || !dims.height) return;
      if (seq !== dimsSyncSeq) return; // a newer pick superseded this one
      var fd;
      try { fd = JSON.parse(fdEl.text()); } catch (_) { return; }
      // vector uploads keep their authored geometry (mirrors the engine)
      if (fd && fd.fileExt && ['pdf', 'ai', 'eps'].indexOf(fd.fileExt) > -1) return;
      fd.fileWidth = dims.width;
      fd.fileHeight = dims.height;
      fd.fileWidth_inch = (dims.width / 300).toFixed(2);
      fd.fileHeight_inch = (dims.height / 300).toFixed(2);
      fd.ratioBy_width = String(dims.width / dims.height);
      fd.ratioBy_height = String(dims.height / dims.width);
      fd.currentWidth = (dims.width / 300).toFixed(2);
      fd.currentHeight = (dims.height / 300).toFixed(2);
      try { if (typeof clampCurrentDimensionsToMax === 'function') clampCurrentDimensionsToMax(fd); } catch (_) {}
      fdEl.html(JSON.stringify(fd));
      var matchedVariant = null;
      try { matchedVariant = getMatchingVariant(fd); } catch (_) {}
      Promise.resolve(applySizes(fd, matchedVariant))
        .then(function () { return calculatePrices(fd, matchedVariant); })
        .then(function () {
          try { if (typeof calculateratioForImage === 'function') calculateratioForImage(); } catch (_) {}
        })
        .catch(function (err) { console.warn('[bg-removal-multi] dims sync:', err && err.message); });
    }).catch(function () { /* candidate URL unreadable — leave sizes as-is */ });
  }

  function trigger(bareUrl, imgixUrl, block) {
    console.log('[bg-removal-multi] trigger', { bareUrl: bareUrl, imgixUrl: imgixUrl });
    var container = getOrCreateTabsContainer(block);
    if (!container) return;

    // The chosen candidate handler — applies the URL to the preview, swaps
    // the chooser\'s active highlight, and records the selection for
    // cart-add. Used by both the scaffold and the final chooser.
    function onSelect(chosen) {
      console.log('[bg-removal-multi] applied', chosen.name);
      /* Prefer the candidate's rect-cropped display URL (alpha-bbox with a
         16/255 threshold, computed in loadCandidate) — imgix's
         trim=colorUnlessAlpha keys off FULLY transparent pixels, so the
         near-invisible alpha speckles AI results leave behind defeated it
         and the hero kept big dead margins (Justin 7-13, moon photo). */
      var visualUrl = (chosen.url && chosen.url.indexOf('http') === 0)
        ? chosen.url
        : (chosen.persistentUrl
          ? (chosen.persistentUrl + (chosen.persistentUrl.indexOf('?') >= 0 ? '&' : '?') + 'trim=colorUnlessAlpha')
          : chosen.url);
      if (chosen.name === 'ix' && chosen.persistentUrl) {
        visualUrl = chosen.persistentUrl;
      }
      /* Remember the pre-upscale visual so the tick's self-heal can rebuild
         the correct hero (with the LIVE upscale state) after an engine
         handler stomps the preview — bySize_popular's SR-toggle rebuilds the
         hero from the ORIGINAL source URL, ignoring the chosen version. */
      var visualBase = visualUrl;
      visualUrl = withUpscaleIfOn(visualUrl);
      applyChoice(visualUrl, block);
      setBgRemovalProperty(chosen);
      if (window.__ntBgRemovalSelected) {
        window.__ntBgRemovalSelected.visualBase = visualBase;
      }
      reflectBgRemoveApplied(block);
      syncSingleUploadDims(chosen, visualUrl);
    }

    // Show the scaffold immediately so the customer sees Option 1 (imgix —
    // the URL the site already painted into the main preview) plus loading
    // spinners for Option 2 and Option 3 while gram/pc resolve in the
    // background. Cuts perceived wait time dramatically.
    var userPicked = false;
    function onSlotPick(chosen) { userPicked = true; onSelect(chosen); }
    renderScaffold(container, imgixUrl, bareUrl, onSlotPick);

    // Seed the cart-add selection with imgix immediately so an Add-to-Cart
    // BEFORE the other models finish still records the right URL + model name.
    setBgRemovalProperty({
      name: 'ix',
      persistentUrl: imgixUrl,
      bareUrl: bareUrl
    });
    reflectBgRemoveApplied(block);

    // Kick off the actual run. The per-candidate-ready callback replaces
    // each scaffold slot\'s spinner with the resolved thumb as it lands.
    run(bareUrl, imgixUrl, null, function (cand) {
      // Ninja AI failed for this source → rebuild the old Option 1/2/3 slots.
      if (cand.ninjaFallback) { renderScaffold(container, imgixUrl, bareUrl, onSlotPick); return; }
      updateScaffoldSlot(container, cand);
      // Ninja AI Rank 1 is the default pick (the ix seed above only covers an
      // add-to-cart before it lands) unless the customer already chose a slot.
      if (cand.name === 'n1' && !userPicked) onSelect(cand);
    }).then(function (result) {
      // Slot diagnostics. Scoring was removed 2026-07-27 in favour of fixed
      // slots, so there is no score/similarity to report — log the pinned
      // position rather than printing score=NaN.
      console.log('[bg-removal-multi] slots (fixed: imgix -> PixelCut -> Ideogram):');
      result.all.forEach(function (c, i) {
        console.log('   Option ' + (i + 1) + ':', c.name);
      });
    }).catch(function (err) {
      console.warn('[bg-removal-multi] failed:', err && err.message);
      hide(block);
    });
  }

  // Is the Remove Background toggle currently on? Checks every plausible
  // selector across the templates we support (DTF Transfers uses the new
  // <toggle-options> + .bgRemover, legacy templates use #option_checkbox).
  // Returns true if ANY visible toggle is checked.
  function isBgRemoveToggleOn() {
    var selectors = [
      'upload-controls toggle-options .bgRemover .toggleOption',
      'multi-upload toggle-options .bgRemover .toggleOption',
      'toggle-options .bgRemover .toggleOption',
      '#option_checkbox'
    ];
    for (var i = 0; i < selectors.length; i++) {
      var nodes = document.querySelectorAll(selectors[i]);
      for (var j = 0; j < nodes.length; j++) {
        if (nodes[j].checked) return true;
      }
    }
    return false;
  }

  // Reflect the APPLIED state back onto the "Remove Background" switch. Rule
  // (Owen): the toggle must mirror reality — whenever a bg-removal IS applied
  // (auto Option 1 seed or an explicit option pick) the switch shows ON and the
  // options stay presented. Without this the picker can sit there with removed
  // previews while the switch reads OFF (it desynced).
  //
  // We set `.checked` directly and do NOT dispatch a synthetic change event:
  //   - the slider thumb is pure CSS (`input:checked + .slider`), so it updates
  //     on its own;
  //   - the cart-add path and the 500ms polling tick both READ `.checked`;
  //   - dispatching could re-enter the polling/trigger logic — avoided.
  // Scoped to the upload block when available (so a multi-upload card only flips
  // its own switch), falling back to document for legacy sibling-toggle markup.
  /* ALPHA-CORNER GATE (Justin 2026-09-11, imgix audit). A PNG whose four
     corners are already transparent has no background to remove. For such
     sources the tick turns the Remove Background switch OFF (same direct
     `.checked` write as reflectBgRemoveApplied, no synthetic event) and does
     NOT start the three-model run — that run costs two premium imgix renders
     (Option 1 + PixelCut) and a Gram Lambda call per opaque-looking source.
     Decided by ONE 64px standard render of the bare source (1/500 credit).
     The customer can switch Remove Background back on: the tick then sees
     toggleOn on the next src change and runs the picker exactly as before.
     JPEG sources never probe. Vector sources (ai/eps/pdf/svg) probe like PNG
     since 2026-09-21: imgix rasterizes them onto a transparent page (alpha 0 at
     all four corners on 12 of 13 real uploads measured), so vector art with
     nothing behind it switches OFF too. Any failure → false → today's behaviour. */
  var alphaCornerCache = new Map();
  function alphaCornersTransparent(bareUrl) {
    if (!/\.(png|webp|tif|tiff|ai|eps|pdf|svg)$/i.test(String(bareUrl || '').split('?')[0])) return Promise.resolve(false);
    if (alphaCornerCache.has(bareUrl)) return alphaCornerCache.get(bareUrl);
    var p = new Promise(function (resolve) {
      try {
        var im = new Image();
        im.crossOrigin = 'anonymous';
        im.onload = function () {
          try {
            var c = document.createElement('canvas'); c.width = 64; c.height = 64;
            var x = c.getContext('2d'); x.drawImage(im, 0, 0, 64, 64);
            var d = x.getImageData(0, 0, 64, 64).data;
            var a = function (px, py) { return d[(py * 64 + px) * 4 + 3]; };
            resolve(a(0, 0) < 16 && a(63, 0) < 16 && a(0, 63) < 16 && a(63, 63) < 16);
          } catch (e) { resolve(false); }
        };
        im.onerror = function () { resolve(false); };
        im.src = String(bareUrl).split('?')[0] + '?fm=png&w=64';
      } catch (e) { resolve(false); }
    });
    alphaCornerCache.set(bareUrl, p);
    return p;
  }
  function reflectBgRemoveOff(block) {
    var selectors = [
      'upload-controls toggle-options .bgRemover .toggleOption',
      'multi-upload toggle-options .bgRemover .toggleOption',
      'toggle-options .bgRemover .toggleOption',
      '#option_checkbox'
    ];
    var scope = (block && block.querySelectorAll) ? block : document;
    for (var i = 0; i < selectors.length; i++) {
      var nodes = scope.querySelectorAll(selectors[i]);
      for (var j = 0; j < nodes.length; j++) if (nodes[j].checked) nodes[j].checked = false;
    }
  }

  function reflectBgRemoveApplied(block) {
    var selectors = [
      'upload-controls toggle-options .bgRemover .toggleOption',
      'multi-upload toggle-options .bgRemover .toggleOption',
      'toggle-options .bgRemover .toggleOption',
      '#option_checkbox'
    ];
    function setIn(scope) {
      var any = false;
      for (var i = 0; i < selectors.length; i++) {
        var nodes = scope.querySelectorAll(selectors[i]);
        for (var j = 0; j < nodes.length; j++) {
          any = true;
          if (!nodes[j].checked) nodes[j].checked = true;
        }
      }
      return any;
    }
    var scoped = block && block.querySelector ? setIn(block) : false;
    if (!scoped) setIn(document);
  }

  // Mirror of getToggleOptionParams() in bySize_popular.js — checks every
  // selector variant the site uses for the Super Resolution / Upscale
  // toggle. When checked, the chosen bg-removal version\'s URL needs
  // `&upscale=true` appended so imgix CDN (which hosts the Lambda-uploaded
  // the model outputs as well as the original imgix bg-remove URL) actually
  // upscales when the cart property and admin link append `&w=2000`.
  function isUpscaleToggleOn() {
    var selectors = [
      'upload-controls toggle-options .superRes .toggleOption',
      'multi-upload toggle-options .superRes .toggleOption',
      'toggle-options .superRes .toggleOption',
      '#superRes'
    ];
    for (var i = 0; i < selectors.length; i++) {
      var nodes = document.querySelectorAll(selectors[i]);
      for (var j = 0; j < nodes.length; j++) {
        if (nodes[j].checked) return true;
      }
    }
    return false;
  }

  // Append `upscale=true` to a URL if the Super Resolution toggle is on
  // and the URL doesn\'t already have it. data: URLs are left alone — they\'re
  // already pixel data, no transformation possible.
  function withUpscaleIfOn(url) {
    if (!url || typeof url !== 'string' || url.indexOf('data:') === 0) return url;
    if (!isUpscaleToggleOn()) return url;
    if (/[?&]upscale=true\b/.test(url)) return url;
    /* imgix CANNOT compose rect= with upscale= — it 200s a fully TRANSPARENT
       png at the rect'd size (verified live 2026-07-31 on a real order photo:
       rect+upscale = 0% opaque; same url minus rect = renders 4x). loadCandidate
       appends a client alpha-bbox rect to display urls when imgix trim leaves
       residual margins, so strip it whenever we add the upscale rider — trim/
       trim-tol stay and compose fine. */
    if (/[?&]rect=/.test(url)) {
      url = url.replace(/([?&])rect=[^&]*&?/, '$1').replace(/[?&]$/, '');
    }
    return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'upscale=true';
  }

  // Build the imgix candidate URL: append `bg-remove=true` + `fm=png` if the
  // src didn't already have them. This is what makes the imgix candidate
  // attempt background removal regardless of the upstream code path.
  // Required because the "use previous design" / "Create with AI" / "Design
  // Studio" paths may set the preview src to a URL that lacks bg-remove —
  // without this we'd score the original (background-intact) image as imgix.
  function ensureImgixBgRemoveParams(src) {
    if (!src) return src;
    // If the preview src is ALREADY a bg-removed imgix URL (standard upload flow:
    // ?trim=colorUnlessAlpha&fm=png&bg-remove=true), use it VERBATIM so the
    // Option 1 thumbnail shares the preview's EXACT warm, transparent derivative.
    // Reconstructing/stripping params below (notably dropping fm=png) produced a
    // DIFFERENT imgix derivative that cold-failed to load, so the thumb fell back
    // through onerror to the opaque bare upload URL — the "Option 1 not
    // transparent / doesn't match the preview" bug.
    if (/[?&]bg-remove=true(?:&|$)/.test(src)) return src;
    // Strip any `fm=` (output-format) param, THEN ensure bg-remove=true. The
    // site\'s own upload flow uses an fm-less imgix URL
    // (…?trim=colorUnlessAlpha&bg-remove=true) for the main preview. On legacy
    // bySize pages the preview img src arrives WITH `&fm=png`; carrying that
    // into the candidate URL produces a DIFFERENT imgix derivative that can
    // return the original (background-intact) image or cold-fail to load —
    // leaving the V1 thumb out of sync with the main preview (it falls back to
    // the background-intact bareUrl). Dropping `fm` makes the candidate URL
    // identical to the preview\'s URL, so they share one warm imgix derivative
    // and always match.
    var parts = src.split('?');
    var base = parts[0];
    var params = parts.length > 1
      ? parts.slice(1).join('?').split('&').filter(function (p) { return p && !/^fm=/.test(p); })
      : [];
    if (params.indexOf('bg-remove=true') === -1) params.push('bg-remove=true');
    return params.length ? base + '?' + params.join('&') : base;
  }

  function bareUrlFromSrc(src) {
    if (!src) return '';
    return src.split('?')[0];
  }

  // The "block" is the smallest container we can scope tabs + applyChoice to,
  // so multi-upload blocks each get their own UI. Falls back to document.
  function findBlock(imgEl) {
    if (!imgEl || !imgEl.closest) return null;
    return imgEl.closest('uploaded-files-block') ||
           imgEl.closest('upload-controls') ||
           imgEl.closest('file-preview') ||
           imgEl.parentElement ||
           null;
  }

  function autoInit() {
    console.log('[bg-removal-multi] auto-init starting');

    // lastSeen: per-img last src — used only to detect "did src change at all".
    // lastTriggered: per-img bareUrl we've already kicked off a run for.
    // Once triggered, we never re-trigger or hide for that img unless a NEW
    // imgix-with-bg-remove URL appears. This prevents the tab-click feedback
    // loop where applyChoice() sets an AI-edit-provider URL and the polling thinks the
    // user toggled bg-removal off.
    var lastSeen = new WeakMap();
    var lastTriggered = new WeakMap();

    // Helper: is the src obviously not a real customer image?
    function isPlaceholderSrc(src) {
      if (!src) return true;
      if (/^data:/.test(src)) return false; // data URLs are our own picks, not placeholders
      // The site\'s default placeholder lives at cdn.shopify.com/.../transparent.png.
      // Treat any URL whose filename starts with `transparent.png` as a placeholder.
      var fname = src.split('?')[0].split('/').pop().toLowerCase();
      return fname.indexOf('transparent.png') === 0;
    }

    function tick() {
      movePreviewNoteAboveImage();
      var imgs = findPreviewImgs();
      if (imgs.length === 0) return;
      // Toggle state drives trigger eligibility (not the URL params), so
      // background removal fires on every source: new uploads, previous
      // designs, Create with AI, Design Studio.
      imgs.forEach(function (img) {
        var src = img.getAttribute('src') || '';
        var prev = lastSeen.get(img) || '';
        if (src === prev) return;
        lastSeen.set(img, src);

        // Toggle eligibility is PER ITEM BLOCK on multiupload. The old
        // global any-toggle-on check let block A's ON state re-trigger a
        // run for block B right after the customer switched B OFF (B's src
        // restore looked like a fresh upload) — the re-run re-applied a
        // candidate and reflectBgRemoveApplied forced B's switch back ON
        // with no change event. Block-scoped check, legacy global fallback
        // for single-upload markups whose toggle lives outside the block.
        var blk = findBlock(img);
        var ownTog = blk && blk !== document &&
          blk.querySelector('.bgRemover .toggleOption, #option_checkbox');
        var toggleOn = ownTog ? !!ownTog.checked : isBgRemoveToggleOn();

        // CLEANUP: when src goes empty / placeholder (customer hit the
        // delete button) or the block's toggle is off, clear any chooser
        // left over from the previous image so it doesn\'t look stale next
        // to a freshly selected design. Also forget our trigger tracking so
        // the next non-placeholder src reliably re-triggers.
        if (!src || isPlaceholderSrc(src) || !toggleOn) {
          // Toggle-off / deleted / placeholder src → hide this block's picker
          // entirely. (Owen 2026-06-23: previously a toggle-off on a real image
          // KEPT the minimized "Show" bar via keepBar; that bar must not linger,
          // so always hide now.)
          hide(blk);
          lastTriggered.delete(img);
          return;
        }

        // Skip tab-click-induced src changes (chosen candidate URLs):
        //  - AI-edit-provider outputs are on the provider's CDN or returned as data: URLs
        //  - our trimmed display URLs are data: URLs
        // Only an HTTP(S) source URL is a real upload/design change.
        if (/^data:/.test(src)) return;

        var bareUrl = bareUrlFromSrc(src);
        if (!bareUrl) return;
        if (lastTriggered.get(img) === bareUrl) {
          /* SELF-HEAL (align option-3 logic, Owen 2026-07-31): the src is
             back on the SOURCE url we already triggered on. If a version was
             picked for this source, an engine handler stomped the hero —
             bySize_popular's SR-toggle rebuilds it from the original source,
             so Option 3 + Upscale previewed the DEFAULT model's render while
             production correctly shipped the chosen one. Re-apply the chosen
             visual with the LIVE upscale state; converges in <=500ms and
             also re-corrects the zoom viewer + size-guide mockups. Loop-safe:
             the healed src's bareUrl differs from the source bareUrl (or,
             when equal, the healed src string equals `desired` and we skip). */
          var healKey = bareUrl.split('/').pop().toLowerCase();
          var healRec = (window.__ntBgRemovalSelectionsByBareUrl || {})[healKey];
          if (healRec && healRec.visualBase) {
            var desired = withUpscaleIfOn(healRec.visualBase);
            if (desired && desired !== src) applyChoice(desired, findBlock(img));
          }
          return;
        }
        // Skip if we previously applied this bareUrl via a version pick —
        // it's a chosen-version URL bubbling back through the src, not a
        // fresh upload / previous-design selection.
        var alreadyApplied = appliedBareUrls.get(img);
        if (alreadyApplied && alreadyApplied.has(bareUrl)) return;
        lastTriggered.set(img, bareUrl);

        // Defensive: clear any existing chooser before kicking off the new
        // run so the previous image\'s V1/V2/V3 never lingers next to the
        // loading indicator (showLoading also clears, but explicit hide
        // covers any edge case where the container reference differs).
        hide(findBlock(img));

        // Always pass an imgix URL with bg-remove params to the run() so the
        // imgix candidate actually performs background removal, even if the
        // source-setting code path (previous designs, AI, etc.) didn't append
        // the bg-remove flag to the displayed src.
        var imgixUrl = ensureImgixBgRemoveParams(src);
        var blk = findBlock(img);
        // Alpha-corner gate (see alphaCornersTransparent): already-transparent
        // art → switch OFF, no picker run. Otherwise the run starts as before.
        alphaCornersTransparent(bareUrl).then(function (transparent) {
          if (lastTriggered.get(img) !== bareUrl) return; // the src moved on meanwhile
          if (transparent) { reflectBgRemoveOff(blk); hide(blk); return; }
          trigger(bareUrl, imgixUrl, blk);
        });
      });
    }

    // The 500ms poll above only reacts to preview-src CHANGES. On the modern
    // dtf-transfers flow, switching Remove Background OFF swaps the preview img
    // back to its original (background-intact) src, so the poll sees the change
    // and hits the `!toggleOn` hide path. Legacy bySize templates (keychains,
    // magnets, etc.) do NOT swap the src on toggle-off, so the poll never fired
    // and the picker lingered. Bind change on ALL toggle markups:
    //   - legacy single upload:  #option_checkbox
    //   - modern single upload:  .bgRemover .toggleOption (bgRemover = parent class)
    //   - multi upload:          #bgRemover / #bgRemover_ — here bgRemover is the
    //     INPUT's id (one per item block), which the parent-class selector never
    //     matched, so multiupload pickers lingered after toggle-off.
    // Use the toggle's own `checked` (per-item truth on multiupload) and scope
    // the hide to that toggle's item block so other items' pickers survive.
    // Re-arm the poll (forget src/trigger tracking) on toggle-on so the picker
    // re-shows, keeping legacy behavior identical to modern.
    function onBgToggleChange(t) {
      if (!t || !t.matches) return;
      var isBgToggle = t.id === 'option_checkbox' ||
                       /^bgRemover/.test(t.id || '') ||
                       t.matches('.bgRemover .toggleOption');
      if (!isBgToggle) return;
      // Everything below is scoped to the toggle's OWN item block. Global
      // actions here (WeakMap wipes, document-wide picker sweeps) are how a
      // toggle on item A re-triggered pipelines / collapsed pickers on item
      // B — confirmed in Justin's 2026-06-10 console capture.
      var scope = (t.closest && (t.closest('uploaded-files-block') ||
                                 t.closest('upload-controls') ||
                                 t.closest('file-preview'))) || document;
      if (t.checked) {
        // Re-arm tracking for THIS block's imgs only, so its pipeline
        // re-runs even though the preview src didn't change on re-enable.
        if (scope === document) {
          lastSeen = new WeakMap();
          lastTriggered = new WeakMap();
        } else {
          scope.querySelectorAll('img').forEach(function (im) {
            lastSeen.delete(im);
            lastTriggered.delete(im);
          });
        }
        // Re-presenting the options: if this block's picker sits minimized
        // (the bar the OFF branch left), expand it via its real Show path.
        var collapsed = scope.querySelectorAll('.nt-bgr-tabs.is-visible.is-collapsed');
        collapsed.forEach(function (c) {
          var btn = c.querySelector('.nt-bgr-hide');
          if (btn) btn.click(); // the real Show path (class + label + aria)
          if (c.classList.contains('is-collapsed')) {
            c.classList.remove('is-collapsed');
            var txt = c.querySelector('.nt-bgr-hide__text');
            if (txt) txt.textContent = 'Hide';
            if (btn) btn.setAttribute('aria-expanded', 'true');
          }
        });
      } else {
        // Minimize — do EXACTLY what that picker's Hide button does (collapse
        // to the header bar, options hidden, "Show" to re-open), per Justin.
        // On single-upload the poll's src-swap path still fully hides right
        // after, so its behavior is unchanged; multiupload (no src swap of
        // its own) settles in the minimized state, which tick's keepBar
        // cleanup preserves. NO document-wide fallback: scoped-find-nothing
        // means this block's picker is already collapsed/absent — doing
        // nothing is correct, sweeping the document collapses other items'.
        // Owen 2026-06-23: HIDE this block's picker entirely (was: minimize to
        // the "Show" bar, which lingered on multiupload). Scoped to this toggle's
        // OWN item block so other items' pickers survive — never a document-wide
        // sweep (see the per-item note above). Mirrors hide(): drop is-visible +
        // clear contents. Turning the toggle back ON re-shows it via the re-armed
        // poll → pipeline re-run → re-render (the t.checked branch above).
        var pickers = (scope.querySelectorAll ? scope : document).querySelectorAll('.nt-bgr-tabs.is-visible');
        pickers.forEach(function (c) {
          c.classList.remove('is-visible');
          c.innerHTML = '';
        });
        // NOTE: no lastTriggered wipe here — that wipe is what let the next
        // tick treat this block's src-restore as a fresh upload and re-run
        // the pipeline (which re-checked the switch via
        // reflectBgRemoveApplied). tick's cleanup branch handles per-img
        // forgetting at the right moment.
      }
    }
    // CAPTURE phase: runs before any theme/modal handler can stopPropagation,
    // so a real tap always reaches us even if the event never bubbles back
    // up to document.
    document.addEventListener('change', function (e) { onBgToggleChange(e.target); }, true);
    // Belt-and-braces: theme flows toggle these inputs via jQuery
    // ($(...).prop('checked', ...).trigger('change')) — jQuery's synthetic
    // trigger does NOT reach native addEventListener handlers, so mirror the
    // handler through jQuery delegation too (idempotent — collapsing an
    // already-collapsed picker is a no-op). jQuery may load after this
    // script, so retry the binding until it appears.
    (function bindJq(tries) {
      if (window.jQuery) {
        window.jQuery(document).on('change', '.toggleOption, #option_checkbox', function () { onBgToggleChange(this); });
      } else if (tries < 40) {
        setTimeout(function () { bindJq(tries + 1); }, 250);
      }
    })(0);

    setInterval(tick, 500);
    tick();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit);
  } else {
    autoInit();
  }
})();
